import { ApplicantTabNavigation } from "@components/layout/ApplicantTabNavigation";
import { ApplicationHeader } from "@components/layout/ApplicationHeader";
import { getApplicationHeaderProps } from "@lib/utils/applicationHeaderHelpers";
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { APPLICANT_CONTENT } from '@/features/applicant/constants/applicantContent'
import { useGoogleTranslateRefresh } from '@/hooks/useGoogleTranslateRefresh'
import {
  Briefcase,
  ArrowRight,
  Save,
  AlertTriangle,
  Plus,
  Info,
  Loader2,
  Eye,
  Trash,
} from "lucide-react";
import type { PlaceResult } from "@/hooks/useGooglePlacesAutocomplete";
import {
  anyDistanceRequiresExplanation,
  buildEmployerAddressString,
  distanceAddressSnapshotForEmployer,
  mergeCalculatedEmploymentDistances,
  needsEmploymentDistanceRecall,
  parseJobDistanceFromApi,
  recalculateAllEmploymentDistances,
  resolveApplicantAddressFromApplication,
  resolvePropertyAddressFromApplication,
  type EmploymentDistanceCheckResult,
} from "@lib/utils/employmentDistanceService";
import { useAppSelector, useAppDispatch } from "@/app/hooks";
import {
  selectApplicationData,
  selectIsReadOnly,
  updateApplication,
  fetchApplicationById,
  fetchApplications,
  mergeBankDetailsFromResponse,
  setApplicationData,
} from "@/features/applicant/applicationSlice";
import { EmployerAddressDetail, type EmployerAddressFields } from "@components/employer/EmployerAddressDetail";
import { DatePicker } from "@components/ui/DatePicker";
import {
  convertToDateInputFormat,
  convertToDisplayFormat,
} from "@lib/utils/dateValidation";
import {
  calendarMonthsBetween,
  employmentMonthsAtJob,
  getMaxCurrentEmploymentStartDate,
  isEmploymentStartDateWithinAllowedFuture,
  isFutureEmploymentStartDate,
  stripAptFromStreetAddress,
  violatesNotFutureDate,
} from "@/features/applicant/utils/addressHelpers";
import { useReferenceData } from "@/hooks/useReferenceData";
import {
  previousEmployerJobGapsEqual,
  recalculatePreviousEmployerJobGaps,
} from "@/features/applicant/utils/employmentJobGap";
import {
  incomeValidationHints,
  type ApplicationFileAttachment,
  type CurrentEmployment,
  type OtherSource,
  type UpdateApplicationRequest,
} from "@/types/application";
import type { NinjaIbvRequestResponse } from "@/lib/api/bankService";
import {
  getResidentOtherSources,
  isBankAlreadyLinked,
  getAttachmentFileName,
} from "@/features/applicant/utils/applicationDataHelpers";
import { parseAddressComponents } from "@lib/utils/addressParser";
import { LinkBankModal } from "@components/bank/LinkBankModal";
import { BankDetailsModal } from "@components/bank/BankDetailsModal";
import { NinjaIframePopup } from "@components/bank/NinjaIframePopup";
import { ValidationModal } from "@components/ui/ValidationModal";
import { ConfirmDialog } from "@components/ui/ConfirmDialog";
import {
  applicantValidationModalProps,
  applicantConfirmDialogProps,
} from "@/features/applicant/constants/applicantDialogProps";
import { SixFileUploadLimitModal } from "@components/ui/SixFileUploadLimitModal";
import { NoEmploymentModal } from "@/features/applicant/components/NoEmploymentModal";
import { IncomeDocumentationSection } from "@/features/applicant/components/IncomeDocumentationSection";
import {
  ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
  isAcceptedIncomeOrIdDocumentFile,
} from "@/lib/utils/acceptedDocumentFile";
import { useBankLinking } from "@/hooks/useBankLinking";
import { shouldShowLinkMyBankForNinjaAccounts } from "@/lib/utils/bankLinking";
import type { RootState } from "@/app/store";
import {
  openFilePreview,
  openFilePreviewByPath,
  generateFilePreviewUrl,
  buildStreamFileUrl,
  ensureFileToken,
  isImageFile,
} from "@/lib/utils/filePreview";
import * as applicationService from "@/lib/api/applicationService";
import { useNavigate, useParams } from "react-router-dom";
import { sendAnalytics } from "@/lib/utils/analytics";
import {
  getTabPathWithApplicantId,
  getTabNavigationOptions,
} from "@lib/utils/applicationNavigation";
import { useTabValidation, useTabAutoSave } from "@/features/applicant/context/TabValidationContext";
import {
  formatCurrencyInput,
  getNumericCurrencyValue,
} from "@/lib/utils/formatters";
import {
  createNinjaIbvRequest,
  createNinjaLinkToken,
  linkNinjaAccount,
} from "@/lib/api/bankService";
import { formatApiError, showInfo, showWarning } from "@/lib/utils/notifications";

import type {
  ApiEmploymentRecord,
  CurrentEmployer,
  PreviousEmployer,
  OtherIncome,
} from "@/features/applicant/types/income";
import {
  shouldOfferEmploymentImport,
  buildEmploymentImportUpdate,
} from "@/features/applicant/utils/employmentImport";



export function Income() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { applicantId: urlApplicantId } = useParams<{ applicantId?: string }>();
  const applicationData = useAppSelector(selectApplicationData);
  const { registerValidator, unregisterValidator } = useTabValidation();

  // Get applicant ID from URL or Redux
  const applicantId = urlApplicantId || applicationData?.applicant_id;
  const isLoading = useAppSelector(
    (state: RootState) => state.application.isLoading,
  );
  const isReadOnly = useAppSelector(selectIsReadOnly);
  const { states, isLoadingStates } = useReferenceData();
  const [hasFetched, setHasFetched] = useState(false);
  const [employmentStatus, setEmploymentStatus] = useState<
    "employed" | "not-employed" | null
  >(null);
  const [currentEmployers, setCurrentEmployers] = useState<CurrentEmployer[]>(
    [],
  );
  const [hasPreviousEmployer, setHasPreviousEmployer] = useState<
    boolean | null
  >(null);
  const [previousEmployers, setPreviousEmployers] = useState<
    PreviousEmployer[]
  >([]);
  const [otherIncomes, setOtherIncomes] = useState<OtherIncome[]>([]);
  const [calculatingDistance, setCalculatingDistance] = useState<string | null>(
    null,
  ); // Track which employer is calculating distance

  // Re-apply Google Translate after employers/other-income sections mount.
  const googleTranslateContentKey = useMemo(
    () =>
      [
        employmentStatus ?? '',
        currentEmployers.length,
        String(hasPreviousEmployer),
        previousEmployers.length,
        otherIncomes.length,
      ].join('|'),
    [
      employmentStatus,
      currentEmployers.length,
      hasPreviousEmployer,
      previousEmployers.length,
      otherIncomes.length,
    ],
  );
  useGoogleTranslateRefresh(googleTranslateContentKey);
  const [, setApplicantToPropertyDistance] = useState<
    number | undefined
  >(undefined);
  const applicantToPropertyDistanceRef = useRef<number | undefined>(undefined);
  const [isRecalculatingDistances, setIsRecalculatingDistances] =
    useState(false);
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({}); // Track saving state per employer
  const [saveMessage, setSaveMessage] = useState<
    Record<string, { type: "success" | "error"; text: string }>
  >({});
  const [otherIncomeErrors, setOtherIncomeErrors] = useState<
    Record<string, Record<string, string>>
  >({}); // Validation errors for other income
  const [isSavingOtherIncome, setIsSavingOtherIncome] = useState<
    Record<string, boolean>
  >({}); // Track saving state per other income
  const [saveOtherIncomeMessage, setSaveOtherIncomeMessage] = useState<
    Record<string, { type: "success" | "error"; text: string }>
  >({});
  // Field-level validation errors for current employers
  const [currentEmployerErrors, setCurrentEmployerErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  // Field-level validation errors for previous employers
  const [previousEmployerErrors, setPreviousEmployerErrors] = useState<
    Record<string, Record<string, string>>
  >({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalMessage, setValidationModalMessage] = useState("");
  const [showNoEmploymentModal, setShowNoEmploymentModal] = useState(false);
  // Import-from-previous-application (employment): Angular importOldata() employmentinfo branch.
  const [showImportPreviousEmploymentDialog, setShowImportPreviousEmploymentDialog] =
    useState(false);
  // Track that the prompt was already offered so it doesn't reappear (Angular showemppopup flag).
  const importEmploymentDialogShown = useRef(false);

  // Helper function to convert field names to user-friendly labels
  const getFieldLabel = (field: string): string => {
    const fieldLabels: Record<string, string> = {
      employmentType: "Type of Employment",
      companyName: "Company Name",
      jobStartDate: "Job Start Date",
      jobEndDate: "Job End Date",
      monthlyIncome: "Monthly Income",
      monthsAtJob: "Months at Job",
      streetName: "Street Name",
      city: APPLICANT_CONTENT.application.sections.currentAddress.city,
      state: APPLICANT_CONTENT.application.sections.currentAddress.state,
      zipCode: "Zip Code",
      country: APPLICANT_CONTENT.application.sections.currentAddress.country,
      distanceExplanation: "Distance Explanation",
      hrManagerEmail: "HR/Manager Email",
      jobGapExplanation: "Job Gap Explanation",
      incomeFrequency: "Income Frequency",
      sourceName: "Source Name",
      amount: "Amount",
      income: "Income",
      transactionDate: "Transaction Date",
      startDate: "Start Date",
      endDate: "End Date",
      monthsOfIncome: "Months of Income",
    };
    return fieldLabels[field] || field;
  };
  // Validation trigger flag
  const [validateForm, setValidateForm] = useState(false);
  // Track deleted job IDs (jobs that were removed and need to be deleted from API)
  const [deletedJobIds, setDeletedJobIds] = useState<number[]>([]);
  // Track if we're saving and continuing (to prevent multiple clicks)
  const [isSavingAndContinuing, setIsSavingAndContinuing] = useState(false);
  const [isSavingExit, setIsSavingExit] = useState(false);

  // Bank linking state
  const [showLinkBankModal, setShowLinkBankModal] = useState(false);
  /** User chose "Use Document Uploads" from link-bank modal (show on-page ninja uploader). */
  const [choseDocumentUploads, setChoseDocumentUploads] = useState(false);
  /** First-time document upload from "Use Document Uploads" opens Supporting Documents in a modal. */
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [showNinjaIframePopup, setShowNinjaIframePopup] = useState(false);
  const [ninjaIbvResponseData, setNinjaIbvResponseData] =
    useState<NinjaIbvRequestResponse | null>(null);
  const [bankMessage, setBankMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [deletingNinjaFileId, setDeletingNinjaFileId] = useState<
    number | string | null
  >(null);
  const [ninjaLastToken, setNinjaLastToken] = useState<string | null>(null);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const tokenApplicantIdRef = useRef<string | number | null>(null);
  // Bank linking state (kept for potential future use in UI)
  const [_linkedBanks, setLinkedBanks] = useState<
    Array<{
      id: string;
      name: string;
      linkedAt: string;
      files: Array<{
        id: string;
        name: string;
        uploadedAt: string;
        filepath?: string;
        attachment_id?: number;
      }>;
      type: "plaid" | "ninja" | "upload";
      pliId?: number;
    }>
  >([]);
  const [_isBankLinked, setIsBankLinked] = useState(false);
  // Proof of Income (general upload – README §6.2)
  const [proofOfIncomeMessage, setProofOfIncomeMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [proofOfIncomeUploading, setProofOfIncomeUploading] = useState(false);
  const [showSixFileUploadLimitModal, setShowSixFileUploadLimitModal] =
    useState(false);

  const isAcceptedProofOfIncomeFile = isAcceptedIncomeOrIdDocumentFile;

  // Bank linking hook
  const bankLinking = useBankLinking({
    applicantId: applicationData?.applicant_id || null,
    onSuccess: async (type) => {
      if (type === "plaid" || type === "ninja") {
        setBankMessage({
          type: "success",
          text: "Bank account linked successfully!",
        });
        setTimeout(() => setBankMessage(null), 5000);
      }
      // Refresh application data to get updated bank info
      if (applicationData?.applicant_id) {
        await dispatch(fetchApplicationById(applicationData.applicant_id));
      }
      setShowNinjaIframePopup(false);
      setNinjaIbvResponseData(null);
    },
    onError: (error) => {
      setBankMessage({ type: "error", text: error });
      setTimeout(() => setBankMessage(null), 5000);
    },
  });

  const hasLinkedBankForUploadReset =
    (bankLinking.ninjaAccounts?.length ?? 0) > 0 ||
    (bankLinking.plaidAccounts?.length ?? 0) > 0;

  useEffect(() => {
    if (hasLinkedBankForUploadReset) {
      setChoseDocumentUploads(false);
    }
  }, [hasLinkedBankForUploadReset]);

  // Fetch application data on mount if not already loaded
  useEffect(() => {
    if (!applicationData?.applicant_id && !isLoading && !hasFetched) {
      setHasFetched(true);
      // Try to get applicant_id from storage
      const storedApplicantId = localStorage.getItem("applicant_id");
      if (storedApplicantId) {
        dispatch(fetchApplicationById(storedApplicantId));
      } else {
        dispatch(fetchApplications());
      }
    }
  }, [applicationData?.applicant_id, isLoading, hasFetched, dispatch]);

  // Fetch latest application data only if we don't have data yet (initial load)
  // When navigating back, Redux already has the latest data from previous updates
  useEffect(() => {
    const currentApplicantId = applicationData?.applicant_id;
    // Only fetch if we have an ID but no employment data (initial load scenario)
    // Don't fetch when navigating back - use Redux data instead
    if (
      currentApplicantId &&
      !applicationData?.current_employment &&
      !applicationData?.other_source
    ) {
      dispatch(fetchApplicationById(currentApplicantId)).catch((error) => {
        showWarning(formatApiError(error));
      });
    }
  }, []); // Only run on mount

  // Scroll to top when component mounts (when user navigates to this page)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  // Load existing current jobs from application data (like About.tsx does for address)
  useEffect(() => {
    if (!applicationData) return;

    // Clear deleted job IDs when data is loaded (to avoid stale deletions)
    setDeletedJobIds([]);

    // Check both current_employment (API format) and current_job (legacy)
    const jobs = (
      applicationData.current_employment || applicationData.current_job || []
    ) as ApiEmploymentRecord[];

    if (jobs.length > 0) {
      const loadedEmployers: CurrentEmployer[] = jobs.map(
        (job: ApiEmploymentRecord, index: number) => {
          // Handle both CurrentEmployment and JobInfo formats
          const jobId =
            typeof job.job_id === "string" && job.job_id !== ""
              ? parseInt(job.job_id)
              : job.job_id || undefined;
          const startDate = job.job_start_date || job.start_date || "";

          // Convert API date (YYYY-MM-DD) to display format (MM/DD/YYYY)
          let displayDate = "";
          if (startDate) {
            if (startDate.includes("/")) {
              // Already in MM/DD/YYYY format
              displayDate = startDate.split(" ")[0];
            } else if (startDate.includes("-")) {
              // Convert YYYY-MM-DD to MM/DD/YYYY
              displayDate = convertToDisplayFormat(startDate.split(" ")[0]);
            }
          }

          // Map job_type back to employmentType - default to 'full-time' if not specified
          let employmentType: "full-time" | "part-time" | "self-employed" | "" =
            "full-time";
          if (job.job_type) {
            switch (job.job_type) {
              case "FULL_TIME":
                employmentType = "full-time";
                break;
              case "PART_TIME":
                employmentType = "part-time";
                break;
              case "SELF_EMPLOYED":
                employmentType = "self-employed";
                break;
              default:
                employmentType = "full-time"; // Default to full-time if unknown type
                break;
            }
          }

          // Get income - could be string or number
          const income = job.income || job.monthly_income || "";
          const incomeNum =
            typeof income === "string" ? parseFloat(income) : income;

          return {
            id: `current-${jobId || index}`,
            jobId: jobId,
            employmentType: employmentType,
            companyName: job.employer_name || "",
            address: job.employer_street_address || "",
            otherOutsideUS:
              job.outside_us === true ||
              job.outside_us === "1" ||
              false,
            monthlyIncome: incomeNum ? incomeNum.toString() : "",
            yearlyCalculatedIncome: incomeNum
              ? `$${(incomeNum * 12).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "",
            jobStartDate: displayDate,
            presentJob:
              job.present_job === "1" ||
              !job.job_end_date,
            jobEndDate:
              job.job_end_date && typeof job.job_end_date === "string"
                ? (job.job_end_date.includes("/")
                    ? job.job_end_date.split(" ")[0]
                    : convertToDisplayFormat(job.job_end_date.split(" ")[0]))
                : undefined,
            monthsAtJob: (() => {
              if (job.job_duration != null) return job.job_duration.toString();
              if (!displayDate) return "";
              // Angular: if start is strictly before today then job_duration = monthsDiff + 1, else job_duration = '0'
              // (start date = today → 0 months, matching Angular's isBefore('day') check)
              // Use local date constructor (not new Date("YYYY-MM-DD") which parses as UTC)
              // to avoid timezone offset making today appear as yesterday.
              const apiDate = convertToAPIDateFormat(displayDate);
              const [y, m, d] = apiDate.split("-").map(Number);
              const startDate = new Date(y, m - 1, d);
              startDate.setHours(0, 0, 0, 0);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return startDate < today ? employmentMonthsAtJob(displayDate).toString() : "0";
            })(),
            hrManagerEmail: job.hr_manager_contact_email || "",
            website: job.employer_website || "",
            jobDistance: parseJobDistanceFromApi(job.job_distance),
            distanceExplanation: job.distance_explain || undefined,
            distanceAddressSnapshot: (() => {
              const parsedDistance = parseJobDistanceFromApi(job.job_distance);
              const employerFields = {
                streetName: job.employer_street || undefined,
                city: job.employer_city || undefined,
                state: job.employer_state || undefined,
                zipCode: job.employer_zip_code
                  ? job.employer_zip_code.toString()
                  : undefined,
                address: job.employer_street_address || undefined,
              };
              return parsedDistance !== undefined
                ? distanceAddressSnapshotForEmployer(employerFields)
                : undefined;
            })(),
            // Address detail fields
            streetName: job.employer_street || undefined,
            aptUnit: undefined,
            city: job.employer_city || undefined,
            state: job.employer_state || undefined,
            zipCode: job.employer_zip_code
              ? job.employer_zip_code.toString()
              : undefined,
            country: job.employer_country || undefined,
          };
        },
      );
      setCurrentEmployers((prev) => {
        const unsaved = prev.filter((emp) => {
          if (emp.jobId) return false;
          // Exclude if this entry now exists in the API response (race condition:
          // jobId may not be set on local state yet when the effect fires after save).
          // Match by company name + start date — same logic as handleSaveCurrentEmployer.
          return !loadedEmployers.some(
            (loaded) =>
              loaded.companyName === emp.companyName &&
              loaded.jobStartDate === emp.jobStartDate,
          );
        });
        return [...loadedEmployers, ...unsaved];
      });
      // Only set employment status if not already set by the employment_status field
      // This prevents overriding the saved status when user selected "Not Employed"
      if (loadedEmployers.length > 0 && !applicationData?.employment_status) {
        setEmploymentStatus("employed");
      }
    } else {
      // Only clear if we don't have any unsaved (new) current employers.
      // Unsaved employers have no jobId. This preserves form data when e.g. bank details
      // popup triggers a merge and API still has no current_employment.
      const hasUnsavedCurrentEmployers = currentEmployersRef.current.some(
        (emp) => !emp.jobId,
      );
      if (!hasUnsavedCurrentEmployers) {
        setCurrentEmployers([]);
      }
    }
  }, [
    applicationData?.current_employment,
    applicationData?.current_job,
    applicationData?.employment_status,
  ]);

  // Ref to track current employers for use in effects (preserve unsaved on bank-details merge)
  const currentEmployersRef = useRef(currentEmployers);
  useEffect(() => {
    currentEmployersRef.current = currentEmployers;
  }, [currentEmployers]);

  // Ref to track previous employers for use in effects without dependency loops
  const previousEmployersRef = useRef(previousEmployers);

  // Sync ref with state
  useEffect(() => {
    previousEmployersRef.current = previousEmployers;
  }, [previousEmployers]);

  // Load existing previous jobs from application data
  useEffect(() => {
    if (!applicationData) return;

    // Check both previous_employment (API format) and previous_job (legacy)
    const jobs = (
      applicationData.previous_employment || applicationData.previous_job || []
    ) as ApiEmploymentRecord[];

    if (jobs.length > 0) {
      const loadedEmployers: PreviousEmployer[] = jobs.map(
        (job: ApiEmploymentRecord, index: number) => {
          // Handle both CurrentEmployment and JobInfo formats
          const jobId =
            typeof job.job_id === "string" && job.job_id !== ""
              ? parseInt(job.job_id)
              : job.job_id || undefined;
          const startDate = job.job_start_date || job.start_date || "";
          const endDate = job.job_end_date || job.end_date || "";

          // Convert API date (YYYY-MM-DD) to display format (MM/DD/YYYY)
          let displayStartDate = "";
          if (startDate) {
            if (startDate.includes("/")) {
              displayStartDate = startDate.split(" ")[0];
            } else if (startDate.includes("-")) {
              displayStartDate = convertToDisplayFormat(
                startDate.split(" ")[0],
              );
            }
          }

          let displayEndDate = "";
          if (endDate) {
            if (endDate.includes("/")) {
              displayEndDate = endDate.split(" ")[0];
            } else if (endDate.includes("-")) {
              displayEndDate = convertToDisplayFormat(endDate.split(" ")[0]);
            }
          }

          // Map job_type back to employmentType
          let employmentType: "full-time" | "part-time" | "self-employed" | "" =
            "";
          if (job.job_type) {
            switch (job.job_type) {
              case "FULL_TIME":
                employmentType = "full-time";
                break;
              case "PART_TIME":
                employmentType = "part-time";
                break;
              case "SELF_EMPLOYED":
                employmentType = "self-employed";
                break;
            }
          }

          // Get income - could be string or number
          const income = job.income || job.monthly_income || "";
          const incomeNum =
            typeof income === "string" ? parseFloat(income) : income;

          return {
            id: `previous-${jobId || index}`,
            jobId: jobId,
            employmentType: employmentType,
            companyName: job.employer_name || "",
            address: job.employer_street_address || "",
            otherOutsideUS:
              job.outside_us === true ||
              job.outside_us === "1" ||
              false,
            monthlyIncome: incomeNum ? incomeNum.toString() : "",
            yearlyCalculatedIncome: incomeNum
              ? `$${(incomeNum * 12).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : "",
            jobStartDate: displayStartDate,
            jobEndDate: displayEndDate,
            monthsAtJob: job.job_duration ? job.job_duration.toString() : "",
            website: job.employer_website || "",
            jobGap:
              job.job_gap !== undefined && job.job_gap !== null
                ? typeof job.job_gap === "string"
                  ? parseInt(job.job_gap, 10)
                  : Number(job.job_gap)
                : undefined,
            jobGapExplanation: job.job_gap_explanation || undefined,
            jobDistance:
              typeof job.job_distance === "number"
                ? job.job_distance
                : typeof job.job_distance === "string" &&
                  job.job_distance !== ""
                  ? parseFloat(job.job_distance)
                  : undefined,
            distanceExplanation: job.distance_explain || undefined,
            // Address detail fields
            streetName: job.employer_street || undefined,
            aptUnit: undefined,
            city: job.employer_city || undefined,
            state: job.employer_state || undefined,
            zipCode: job.employer_zip_code
              ? job.employer_zip_code.toString()
              : undefined,
            country: job.employer_country || undefined,
          };
        },
      );
      setPreviousEmployers(loadedEmployers);
      setHasPreviousEmployer(true);
    } else {
      // Only clear if we don't have any unsaved (new) employers
      // Unsaved employers have an ID that doesn't match the API pattern or no jobId
      const hasUnsavedEmployers = previousEmployersRef.current.some(
        (emp) => !emp.jobId,
      );

      if (!hasUnsavedEmployers) {
        setPreviousEmployers([]);
        if (applicationData.previous_employer === "0") {
          setHasPreviousEmployer(false);
        }
      }
    }
  }, [
    applicationData?.previous_employment,
    applicationData?.previous_job,
    applicationData?.previous_employer,
  ]);

  // Angular getTotalMonths() parity: recompute every previous employer's job_gap in one
  // pass on any employment change, clearing the explanation when the gap drops below 1
  // month, so gaps stay correct regardless of row order (e.g. chronological imports). The
  // signature includes each row's jobGap so a refetch's stale gap (often 0) can't overwrite
  // the computed value; recompute converges, so this never loops. See utils/employmentJobGap.ts.
  const previousEmployerGapInputSignature = previousEmployers
    .map((emp) => `${emp.jobStartDate}|${emp.jobEndDate}|${emp.jobGap}`)
    .join(";");
  const currentEmployerStartSignature = currentEmployers
    .map((emp) => emp.jobStartDate)
    .join(";");

  // useLayoutEffect (not useEffect): correct a stale seeded gap before paint, avoiding a
  // brief 0 → correct-gap flicker on import / autosave-refetch.
  useLayoutEffect(() => {
    setPreviousEmployers((prev) => {
      const recalculated = recalculatePreviousEmployerJobGaps(
        prev,
        currentEmployers,
      );
      return previousEmployerJobGapsEqual(prev, recalculated)
        ? prev
        : recalculated;
    });
    // Signatures capture every input; currentEmployers is read via the functional update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousEmployerGapInputSignature, currentEmployerStartSignature]);

  // Load employment status from API - ensure sections are visible when data exists
  useEffect(() => {
    if (!applicationData) return;

    // Check for other_source in both possible field names (other_source or resident_other_source)
    const otherSourceData = getResidentOtherSources(applicationData);
    const hasCurrentEmployment =
      applicationData.current_employment &&
      applicationData.current_employment.length > 0;
    const hasOtherSource = otherSourceData && otherSourceData.length > 0;

    // Determine employment status based on API data
    // Priority: Check saved employment_status field first, then infer from data
    if (
      applicationData.employment_status !== undefined &&
      applicationData.employment_status !== null &&
      applicationData.employment_status !== ""
    ) {
      // API has explicit status: "1" = employed, "0" or other = not-employed
      const apiStatus = String(applicationData.employment_status);
      if (apiStatus === "1" || apiStatus === "true") {
        setEmploymentStatus("employed");
      } else if (apiStatus === "0" || apiStatus === "false") {
        setEmploymentStatus("not-employed");
      }
      // If status is set, use it and return (don't override with data inference)
      return;
    }

    // If no explicit status, infer from data
    if (hasCurrentEmployment) {
      // If there's current employment data, set status to employed to show the section
      setEmploymentStatus("employed");
    } else if (hasOtherSource) {
      // If there's other source data but no current employment, set to not-employed
      // This ensures the Other Income section is visible (it's always visible, but status helps)
      setEmploymentStatus("not-employed");
    }
    // If no data and no status, leave it as null (user hasn't selected yet)
  }, [
    applicationData?.employment_status,
    applicationData?.current_employment,
    applicationData?.resident_other_source,
    applicationData?.other_source,
  ]);

  // Offer to import employment from a previous application while current employment is
  // empty (Angular importOldata, edit-mode self-serve only).
  useEffect(() => {
    if (importEmploymentDialogShown.current) return;
    if (!shouldOfferEmploymentImport(applicationData, isReadOnly)) return;
    importEmploymentDialogShown.current = true;
    setShowImportPreviousEmploymentDialog(true);
  }, [applicationData, isReadOnly]);

  // Apply the chosen import; dispatched into Redux so the load effects re-map it into form
  // state (Angular setOldApplicationData → setPrefillFormData).
  const applyEmploymentImport = (mode: "current" | "previous") => {
    setShowImportPreviousEmploymentDialog(false);
    if (!applicationData) return;
    const next = buildEmploymentImportUpdate(applicationData, mode);
    if (!next) return;
    dispatch(setApplicationData(next));
    showInfo("Added Employment data");
  };

  // Register validator for Tab Validation
  useEffect(() => {
    const validateIncomeTab = (): { isValid: boolean; message?: string } => {
      const errorMessages: string[] = [];

      // 1. Employment Status
      if (employmentStatus === null) {
        errorMessages.push("Please indicate your employment status.");
      } else if (employmentStatus === "employed") {
        if (currentEmployers.length === 0) {
          errorMessages.push("At least one current employer is required.");
        } else {
          currentEmployers.forEach((emp, index) => {
            const prefix = `Current Employer ${index + 1}: `;
            if (!emp.companyName || !emp.companyName.trim())
              errorMessages.push(`${prefix}Company Name is required.`);
            if (!emp.monthlyIncome || !emp.monthlyIncome.trim())
              errorMessages.push(`${prefix}Monthly Income is required.`);
            if (!emp.jobStartDate || !emp.jobStartDate.trim())
              errorMessages.push(`${prefix}Job Start Date is required.`);
            if (!emp.employmentType)
              errorMessages.push(`${prefix}Employment Type is required.`);
            if (!emp.city || !emp.city.trim())
              errorMessages.push(`${prefix}City is required.`);
            if (!emp.state || !emp.state.trim())
              errorMessages.push(`${prefix}State is required.`);

            if (
              currentEmployerRequiresDistanceWarning(emp) &&
              (!emp.distanceExplanation || !emp.distanceExplanation.trim())
            ) {
              errorMessages.push(
                `${prefix}Explanation for distance > 50 miles is required.`,
              );
            }
          });
        }
      }

      // 2. Previous Employer
      if (hasPreviousEmployer === null) {
        errorMessages.push("Please indicate if you have a previous employer.");
      } else if (hasPreviousEmployer === true) {
        if (previousEmployers.length === 0) {
          errorMessages.push("At least one previous employer is required.");
        } else {
          previousEmployers.forEach((emp, index) => {
            const prefix = `Previous Employer ${index + 1}: `;
            if (!emp.companyName || !emp.companyName.trim())
              errorMessages.push(`${prefix}Company Name is required.`);
            if (!emp.monthlyIncome || !emp.monthlyIncome.trim())
              errorMessages.push(`${prefix}Monthly Income is required.`);
            if (!emp.jobStartDate || !emp.jobStartDate.trim())
              errorMessages.push(`${prefix}Job Start Date is required.`);
            if (!emp.jobEndDate || !emp.jobEndDate.trim())
              errorMessages.push(`${prefix}Job End Date is required.`);
          });
        }
      }

      // 3. Other sources of income (Angular validates other_source on tab change).
      otherIncomes.forEach((income, index) => {
        const prefix = `Other Source of Income ${index + 1}: `;
        const incomeErrors = validateOtherIncome(income);
        const uniqueMessages = Array.from(
          new Set(Object.values(incomeErrors).filter(Boolean)),
        );
        uniqueMessages.forEach((message) =>
          errorMessages.push(`${prefix}${message}`),
        );
      });

      if (errorMessages.length > 0) {
        return {
          isValid: false,
          message: errorMessages.join("\n"),
        };
      }

      return { isValid: true };
    };

    registerValidator("income", validateIncomeTab);
    return () => unregisterValidator("income");
    // validateOtherIncome is a stable component helper; otherIncomes drives re-registration.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    registerValidator,
    unregisterValidator,
    employmentStatus,
    currentEmployers,
    hasPreviousEmployer,
    previousEmployers,
    otherIncomes,
  ]);

  // Generate Ninja link token automatically when application data loads (if bank not already linked)
  // As per documentation: "Automatically when application data is loaded (if !applicationData.bank_already_link)"
  useEffect(() => {
    const generateTokenNinja = async () => {
      if (!applicationData?.applicant_id || isGeneratingToken) {
        return;
      }

      // Only generate token if bank is not already linked (as per documentation)
      if (isBankAlreadyLinked(applicationData)) {
        return;
      }

      // Only generate token if applicant_id changed or we don't have a token yet
      if (
        tokenApplicantIdRef.current === applicationData.applicant_id &&
        ninjaLastToken
      ) {
        return;
      }

      setIsGeneratingToken(true);
      try {
        const response = await createNinjaLinkToken(
          applicationData.applicant_id,
        );
        if (response) {
          setNinjaLastToken(response);
          tokenApplicantIdRef.current = applicationData.applicant_id;
        }
      } catch {
        showWarning(
          "Unable to prepare bank linking. You can try again when linking your account.",
        );
      } finally {
        setIsGeneratingToken(false);
      }
    };

    generateTokenNinja();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    applicationData?.applicant_id,
    applicationData?.bank_already_link,
  ]);

  // Load other income from API when application data is available
  useEffect(() => {
    if (!applicationData) return;

    // Get other income from both possible field names (resident_other_source or other_source)
    // API may return it as resident_other_source
    const otherSources = getResidentOtherSources(applicationData);

    if (otherSources.length > 0) {
      const loadedOtherIncomes: OtherIncome[] = otherSources.map(
        (os: OtherSource, index: number) => {
          const jobId =
            typeof os.job_id === "string" && os.job_id !== ""
              ? parseInt(os.job_id)
              : os.job_id || undefined;

          // Convert API date (YYYY-MM-DD) to display format (MM/DD/YYYY)
          let displayStartDate = "";
          if (os.job_start_date) {
            const dateStr = os.job_start_date.split(" ")[0]; // Remove time if present
            if (dateStr.includes("/")) {
              displayStartDate = dateStr;
            } else if (dateStr.includes("-")) {
              displayStartDate = convertToDisplayFormat(dateStr);
            }
          }

          let displayEndDate = "";
          if (os.job_end_date) {
            const dateStr = os.job_end_date.split(" ")[0]; // Remove time if present
            if (dateStr.includes("/")) {
              displayEndDate = dateStr;
            } else if (dateStr.includes("-")) {
              displayEndDate = convertToDisplayFormat(dateStr);
            }
          }

          // Map income_period back to frequency
          let incomeFrequency: OtherIncome["incomeFrequency"] = "monthly";
          if (os.income_period) {
            switch (os.income_period) {
              case "Per_Month":
                incomeFrequency = "monthly";
                break;
              case "One_Time_Payment":
              case "One_Time":
                incomeFrequency = "one-time";
                break;
              // Reverted: API might return weekly/bi-monthly/yearly from legacy apps, we map them to empty to force user interaction
              case "Per_Week":
              case "Per_Bi_Monthly":
              case "Per_Year":
                incomeFrequency = "";
                break;
              default:
                incomeFrequency = "monthly";
            }
          }

          // Get income amount
          const income = os.income || "";
          const incomeNum =
            typeof income === "string" ? parseFloat(income) : income;
          const incomeStr = incomeNum ? incomeNum.toString() : "";

          // Calculate yearly income
          const yearly = calculateYearlyIncome(incomeStr, incomeFrequency);

          // Check if currently active - present_job is string "1" for active, "0" for inactive
          // If present_job is not provided, check if job_end_date is null (means still active)
          // For one-time income, it's typically not "active" in the same way
          const currentlyActive =
            os.present_job === "1" ||
            (incomeFrequency === "monthly" &&
              !os.job_end_date &&
              os.present_job !== "0");

          // For one-time, use job_start_date as transactionDate
          const transactionDate =
            incomeFrequency === "one-time" ? displayStartDate : "";
          const amount = incomeFrequency === "one-time" ? incomeStr : "";

          return {
            id: jobId ? `other-${jobId}` : `other-${index}-${Date.now()}`,
            incomeFrequency: incomeFrequency,
            sourceName: os.source_name || "",
            income: incomeStr,
            yearlyCalculatedIncome: yearly,
            startDate: displayStartDate,
            endDate: displayEndDate,
            currentlyActive: currentlyActive,
            monthsOfIncome: os.job_duration ? os.job_duration.toString() : "",
            transactionDate: transactionDate,
            amount: amount,
          };
        },
      );
      setOtherIncomes(loadedOtherIncomes);
    } else {
      // Clear saved items (those with jobId) but keep unsaved items (those without jobId)
      setOtherIncomes((prev) =>
        prev.filter((inc) => {
          // Keep items that don't have a jobId (unsaved/new items)
          return !inc.id.match(/^other-(\d+)$/);
        }),
      );
    }
  }, [
    applicationData?.resident_other_source,
    applicationData?.other_source,
    applicationData,
  ]);

  // Load bank data from API when application data is available
  useEffect(() => {
    if (!applicationData?.applicant_id) return;

    // Combine Plaid and Ninja accounts
    const allAccounts: Array<{
      id: string;
      name: string;
      linkedAt: string;
      files: Array<{
        id: string;
        name: string;
        uploadedAt: string;
        filepath?: string;
        attachment_id?: number;
      }>;
      type: "plaid" | "ninja" | "upload";
      pliId?: number;
    }> = [];

    // Add Plaid accounts
    bankLinking.plaidAccounts.forEach((account) => {
      allAccounts.push({
        id: `plaid-${account.pli_id}`,
        name: account.ins_name,
        linkedAt: new Date().toISOString(),
        files: [],
        type: "plaid",
        pliId: account.pli_id,
      });
    });

    // Add Ninja accounts
    bankLinking.ninjaAccounts.forEach((account) => {
      allAccounts.push({
        id: `ninja-${account.pli_id}`,
        name: account.bank_name,
        linkedAt: new Date().toISOString(),
        files: [],
        type: "ninja",
        pliId: account.pli_id,
      });
    });

    // Get bank data from application response (uploaded files)
    const bankName = applicationData.bank_name;
    const ninjaIncomeProof = applicationData.ninja_income_proof || [];

    // If we have uploaded files, add them
    if (
      bankName ||
      (Array.isArray(ninjaIncomeProof) && ninjaIncomeProof.length > 0)
    ) {
      const files = Array.isArray(ninjaIncomeProof)
        ? ninjaIncomeProof.map(
          (
            file: {
              attachment_id?: number;
              filename?: string;
              name?: string;
              created_at?: string | number;
              filepath?: string;
            },
            fileIndex: number,
          ) => ({
            id: `file-${file.attachment_id || fileIndex}`,
            name: file.filename || file.name || "document",
            uploadedAt: file.created_at
              ? typeof file.created_at === "number"
                ? new Date(file.created_at * 1000).toISOString()
                : new Date(file.created_at).toISOString()
              : new Date().toISOString(),
            filepath: file.filepath || "",
            attachment_id: file.attachment_id,
          }),
        )
        : [];

      if (files.length > 0) {
        // Check if bank already exists
        const existingBankIndex = allAccounts.findIndex(
          (bank) => bank.name === bankName,
        );
        if (existingBankIndex >= 0) {
          allAccounts[existingBankIndex].files = files;
        } else {
          allAccounts.push({
            id: `upload-${bankName || "default"}`,
            name: bankName || "Bank",
            linkedAt: files[0]?.uploadedAt || new Date().toISOString(),
            files: files,
            type: "upload",
          });
        }
      }
    }

    setLinkedBanks(allAccounts);
    setIsBankLinked(allAccounts.length > 0);
  }, [
    applicationData?.applicant_id,
    bankLinking.plaidAccounts,
    bankLinking.ninjaAccounts,
    applicationData?.bank_name,
    applicationData?.ninja_income_proof,
  ]);

  // Map employment type to API format
  const mapEmploymentType = (type: string): string => {
    switch (type) {
      case "full-time":
        return "FULL_TIME";
      case "part-time":
        return "PART_TIME";
      case "self-employed":
        return "SELF_EMPLOYED";
      default:
        return "FULL_TIME";
    }
  };

  // Convert MM/DD/YYYY to YYYY-MM-DD format
  const convertToAPIDateFormat = (dateStr: string): string => {
    if (!dateStr) return "";
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
    // If already in YYYY-MM-DD format, return as is
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    return "";
  };

  // Build job data for API - matches Angular employmentinfo.current_employment payload (getControls('current_employment'), distance_explain/job_distance when > 50)
  const buildJobData = (employer: CurrentEmployer): CurrentEmployment => {
    const monthlyIncomeNum =
      parseFloat(getNumericValue(employer.monthlyIncome)) || 0;
    const jobDuration = isFutureEmploymentStartDate(employer.jobStartDate)
      ? 0
      : parseInt(employer.monthsAtJob) || 0;

    // Build street address: unit under address section (Street Unit, City, State, Zip, Country)
    const formatAptForAddressPrev = (unit: string) => {
      const t = unit.trim();
      return t ? (t.startsWith("#") ? t : `#${t}`) : "";
    };
    let streetAddress = employer.address;
    if (employer.streetName) {
      const streetPart = employer.streetName;
      const aptFormatted = employer.aptUnit ? formatAptForAddressPrev(employer.aptUnit) : "";
      const addressSection = aptFormatted ? `${streetPart} ${aptFormatted}` : streetPart;
      // Zip code before country: City, State, Zip, Country
      const locationParts = [
        employer.city,
        employer.state,
        employer.zipCode,
        employer.country,
      ].filter(Boolean);
      const locationPart =
        locationParts.length > 0 ? `, ${locationParts.join(", ")}` : "";
      streetAddress = `${addressSection}${locationPart}`;
    } else if (employer.aptUnit && streetAddress) {
      const baseAddress = stripAptFromStreetAddress(streetAddress.trim(), null);
      const unitFormatted = formatAptForAddressPrev(employer.aptUnit);
      streetAddress = `${baseAddress} ${unitFormatted}`;
    }

    // Build employer_street (street name only)
    const employerStreet = employer.streetName || "";

    // Keep zip as string so leading zeros are preserved in the API payload (e.g. 007721)
    const zipCodeValue = employer.zipCode
      ? String(employer.zipCode).trim()
      : undefined;

    // Angular parity: present_job "1"/"0", job_end_date when job ended; outside_us as "1"/"0"
    const presentJob = employer.presentJob !== false;
    return {
      job_id: employer.jobId ? employer.jobId : "", // Empty string for new, number for existing
      job_type: mapEmploymentType(employer.employmentType),
      employer_name: employer.companyName,
      employer_street_address: streetAddress,
      outside_us: employer.otherOutsideUS ? "1" : "0",
      employer_street: employerStreet || undefined,
      employer_zip_code: zipCodeValue,
      employer_city: employer.city || undefined,
      employer_country: employer.country || undefined,
      employer_state: employer.state || undefined,
      income: monthlyIncomeNum > 0 ? monthlyIncomeNum.toString() : undefined,
      income_period: "Per_Month", // Monthly income (Angular: Per_Month)
      job_duration: jobDuration > 0 ? jobDuration : 0,
      job_start_date: employer.jobStartDate
        ? convertToAPIDateFormat(employer.jobStartDate)
        : undefined,
      check_types: "",
      reference_contact: "",
      employer_website: employer.website || undefined,
      hr_manager_contact_email: employer.hrManagerEmail ?? "",
      present_job: presentJob ? "1" : "0",
      job_end_date: presentJob
        ? null
        : employer.jobEndDate
          ? convertToAPIDateFormat(employer.jobEndDate)
          : undefined,
      distance_explain: employer.distanceExplanation ?? "",
      job_distance: employer.jobDistance ?? undefined,
    };
  };

  // Build previous job data for API (matching the curl structure)
  const buildPreviousJobData = (
    employer: PreviousEmployer,
    currentJobStartDate?: string,
  ): CurrentEmployment => {
    const monthlyIncomeNum =
      parseFloat(getNumericValue(employer.monthlyIncome)) || 0;
    const jobDuration = parseInt(employer.monthsAtJob) || 0;

    // Build street address: unit under address section (Street Unit, City, State, Zip, Country)
    const formatAptForAddressPrev = (unit: string) => {
      const t = unit.trim();
      return t ? (t.startsWith("#") ? t : `#${t}`) : "";
    };
    let streetAddress = employer.address;
    if (employer.streetName) {
      const streetPart = employer.streetName;
      const aptFormatted = employer.aptUnit ? formatAptForAddressPrev(employer.aptUnit) : "";
      const addressSection = aptFormatted ? `${streetPart} ${aptFormatted}` : streetPart;
      // Zip code before country: City, State, Zip, Country
      const locationParts = [
        employer.city,
        employer.state,
        employer.zipCode,
        employer.country,
      ].filter(Boolean);
      const locationPart =
        locationParts.length > 0 ? `, ${locationParts.join(", ")}` : "";
      streetAddress = `${addressSection}${locationPart}`;
    } else if (employer.aptUnit && streetAddress) {
      const baseAddress = stripAptFromStreetAddress(streetAddress.trim(), null);
      const unitFormatted = formatAptForAddressPrev(employer.aptUnit);
      streetAddress = `${baseAddress} ${unitFormatted}`;
    }

    // Build employer_street (street name only)
    const employerStreet = employer.streetName || "";

    // Keep zip as string so leading zeros are preserved in the API payload (e.g. 007721)
    const zipCodeValue = employer.zipCode
      ? String(employer.zipCode).trim()
      : undefined;

    // Job gap: use state value or recalc with same logic as UI (Angular: calendar months between end and next start)
    let jobGap = employer.jobGap;
    if (
      currentJobStartDate &&
      employer.jobEndDate &&
      employer.jobGap === undefined
    ) {
      jobGap = calendarMonthsBetween(
        employer.jobEndDate,
        currentJobStartDate,
      );
    }

    // Angular parity: outside_us as "1"/"0"; no present_job for previous_employment
    return {
      job_id: employer.jobId ? employer.jobId : "", // Empty string for new, number for existing
      job_type: mapEmploymentType(employer.employmentType),
      employer_name: employer.companyName,
      employer_street_address: streetAddress,
      outside_us: employer.otherOutsideUS ? "1" : "0",
      employer_street: employerStreet || undefined,
      employer_zip_code: zipCodeValue,
      employer_city: employer.city || undefined,
      employer_country: employer.country || undefined,
      employer_state: employer.state || undefined,
      income: monthlyIncomeNum > 0 ? monthlyIncomeNum.toString() : undefined,
      income_period: "Per_Month", // Monthly income (Angular: Per_Month)
      job_duration: jobDuration > 0 ? jobDuration : 0,
      job_start_date: employer.jobStartDate
        ? convertToAPIDateFormat(employer.jobStartDate)
        : undefined,
      job_end_date: employer.jobEndDate
        ? convertToAPIDateFormat(employer.jobEndDate)
        : undefined, // Required for previous employment
      check_types: "",
      reference_contact: "",
      employer_website: employer.website || undefined,
      distance_explain: employer.distanceExplanation ?? "",
      job_distance: employer.jobDistance ?? undefined,
      job_gap: jobGap,
      job_gap_explanation: employer.jobGapExplanation ?? "",
      // Note: present_job should NOT be included for previous_employment (Angular parity)
    };
  };

  // Validate current employer fields
  const validateCurrentEmployer = (
    employer: CurrentEmployer,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!employer.employmentType) {
      errors.employmentType = APPLICANT_CONTENT.errors.employment.employmentType;
    }

    if (!employer.companyName || !employer.companyName.trim()) {
      errors.companyName = APPLICANT_CONTENT.errors.employment.companyName;
    } else {
      const isDuplicate = currentEmployers.some(
        (emp) =>
          emp.id !== employer.id &&
          emp.companyName.trim().toLowerCase() ===
          employer.companyName.trim().toLowerCase(),
      );
      if (isDuplicate) {
        errors.companyName = "This employer already exists.";
      }
    }

    if (!employer.jobStartDate) {
      errors.jobStartDate = APPLICANT_CONTENT.errors.employment.jobStartDate;
    } else if (!isEmploymentStartDateWithinAllowedFuture(employer.jobStartDate)) {
      errors.jobStartDate =
        "Job Start Date cannot be more than 90 days in the future.";
    }

    if (!employer.monthlyIncome || !getNumericValue(employer.monthlyIncome)) {
      errors.monthlyIncome = APPLICANT_CONTENT.errors.employment.monthlyIncome;
    }

    if (
      !isFutureEmploymentStartDate(employer.jobStartDate) &&
      (!employer.monthsAtJob || !employer.monthsAtJob.trim())
    ) {
      errors.monthsAtJob = APPLICANT_CONTENT.errors.employment.monthsAtJob;
    }

    // Address validation
    if (!employer.streetName || !employer.streetName.trim()) {
      errors.streetName = APPLICANT_CONTENT.errors.employment.streetName;
    }

    if (!employer.city || !employer.city.trim()) {
      errors.city = APPLICANT_CONTENT.errors.employment.city;
    }

    if (!employer.state || !employer.state.trim()) {
      errors.state = APPLICANT_CONTENT.errors.employment.state;
    }

    if (!employer.zipCode || !employer.zipCode.trim()) {
      errors.zipCode = APPLICANT_CONTENT.errors.employment.zipCode;
    }

    if (!employer.country || !employer.country.trim()) {
      errors.country = APPLICANT_CONTENT.errors.employment.country;
    }

    // Distance explanation validation
    if (
      currentEmployerRequiresDistanceWarning(employer) &&
      (!employer.distanceExplanation || !employer.distanceExplanation.trim())
    ) {
      errors.distanceExplanation = APPLICANT_CONTENT.errors.employment.distanceExplanation;
    }

    // Email validation
    if (employer.hrManagerEmail && employer.hrManagerEmail.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(employer.hrManagerEmail.trim())) {
        errors.hrManagerEmail =
          "'HR/Manager Contact Email' must be a valid email address.";
      }
    }

    return errors;
  };

  // Validate previous employer fields
  const validatePreviousEmployer = (
    employer: PreviousEmployer,
  ): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!employer.employmentType) {
      errors.employmentType = APPLICANT_CONTENT.errors.employment.employmentType;
    }

    if (!employer.companyName || !employer.companyName.trim()) {
      errors.companyName = APPLICANT_CONTENT.errors.employment.companyName;
    }

    if (!employer.jobStartDate) {
      errors.jobStartDate = APPLICANT_CONTENT.errors.employment.jobStartDate;
    } else if (violatesNotFutureDate(employer.jobStartDate)) {
      // Angular notfutureDateValidator: a previous job must start strictly before today.
      errors.jobStartDate = "Date should not be in the Future.";
    }

    if (!employer.jobEndDate) {
      errors.jobEndDate = APPLICANT_CONTENT.errors.employment.jobEndDate;
    } else if (employer.jobStartDate && employer.jobEndDate) {
      const start = new Date(convertToAPIDateFormat(employer.jobStartDate));
      const end = new Date(convertToAPIDateFormat(employer.jobEndDate));
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      if (start > end) {
        errors.jobEndDate = "Job End Date should be after Job Start Date";
      }
    }

    if (!employer.monthlyIncome || !getNumericValue(employer.monthlyIncome)) {
      errors.monthlyIncome = APPLICANT_CONTENT.errors.employment.monthlyIncome;
    }

    if (!employer.monthsAtJob || !employer.monthsAtJob.trim()) {
      errors.monthsAtJob = APPLICANT_CONTENT.errors.employment.monthsAtJob;
    }

    // Address validation
    if (!employer.streetName || !employer.streetName.trim()) {
      errors.streetName = APPLICANT_CONTENT.errors.employment.streetName;
    }

    if (!employer.city || !employer.city.trim()) {
      errors.city = APPLICANT_CONTENT.errors.employment.city;
    }

    if (!employer.state || !employer.state.trim()) {
      errors.state = APPLICANT_CONTENT.errors.employment.state;
    }

    if (!employer.zipCode || !employer.zipCode.trim()) {
      errors.zipCode = APPLICANT_CONTENT.errors.employment.zipCode;
    }

    if (!employer.country || !employer.country.trim()) {
      errors.country = APPLICANT_CONTENT.errors.employment.country;
    }

    // Job gap explanation validation
    if (
      employer.jobGap !== undefined &&
      employer.jobGap >= 1 &&
      !employer.jobGapExplanation
    ) {
      errors.jobGapExplanation =
        "'Job Gap Explanation' is required when gap is 1 month or more.";
    }

    return errors;
  };

  // Handle save current employer
  const handleSaveCurrentEmployer = async (employer: CurrentEmployer) => {
    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    if (!applicationData?.applicant_id) {
      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: { type: "error", text: "Application ID not found" },
      }));
      return;
    }

    // Validate fields
    const errors = validateCurrentEmployer(employer);
    if (Object.keys(errors).length > 0) {
      setCurrentEmployerErrors((prev) => ({
        ...prev,
        [employer.id]: errors,
      }));
      setValidateForm(true);
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      Object.entries(errors).forEach(([field, message]) => {
        const fieldLabel = getFieldLabel(field);
        errorMessages.push(`${fieldLabel}: ${message}`);
      });
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setShowValidationModal(true);
      }
      return;
    }

    // Clear errors if validation passes
    setCurrentEmployerErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[employer.id];
      return newErrors;
    });

    setIsSaving((prev) => ({ ...prev, [employer.id]: true }));

    try {
      // Recalculate distance on save only when missing or employer address changed
      let employerForSave = employer;
      if (needsEmploymentDistanceRecall(employer)) {
        employerForSave = await refreshEmployerDistanceBeforeSave(employer);
        setCurrentEmployers((prev) =>
          prev.map((emp) =>
            emp.id === employer.id ? employerForSave : emp,
          ),
        );

        const errorsAfterDistance = validateCurrentEmployer(employerForSave);
        if (Object.keys(errorsAfterDistance).length > 0) {
          setCurrentEmployerErrors((prev) => ({
            ...prev,
            [employer.id]: errorsAfterDistance,
          }));
          setValidateForm(true);
          const errorMessages: string[] = [];
          Object.entries(errorsAfterDistance).forEach(([field, message]) => {
            errorMessages.push(`${getFieldLabel(field)}: ${message}`);
          });
          setValidationModalMessage(errorMessages.join("\n"));
          setShowValidationModal(true);
          return;
        }
      }

      // Build job data
      const jobData = buildJobData(employerForSave);

      // Get existing jobs (Angular parity: API may return current_employment or current_job)
      const currentJobsFromApi =
        applicationData.current_employment || applicationData.current_job || [];
      const existingJobs = currentJobsFromApi.filter((job: CurrentEmployment) => {
        const jobIdNum =
          typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
        return jobIdNum && !deletedJobIds.includes(jobIdNum);
      });

      // Update or add job
      let updatedJobs: CurrentEmployment[];
      if (employer.jobId) {
        // Update existing job - find by job_id
        updatedJobs = existingJobs.map((job: CurrentEmployment) => {
          const jobIdNum =
            typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
          return jobIdNum === employer.jobId ? jobData : job;
        });
      } else {
        // Add new job
        updatedJobs = [...existingJobs, jobData];
      }

      // Prepare update data matching Angular employmentinfo payload
      // Preserve previous_employer so we don't wipe it when saving only current employment
      const previousEmployerValue =
        applicationData.previous_employer ??
        (applicationData.previous_employment?.length ||
        applicationData.previous_job?.length
          ? "1"
          : "0");
      const updateData: UpdateApplicationRequest = {
        ...incomeValidationHints("section"),
        employment_status: "1", // "1" for employed
        previous_employer: previousEmployerValue,
        ninja_reason: applicationData.ninja_reason ?? "",
        underwriter_verified_income_other:
          applicationData.underwriter_verified_income_other ?? "0",
        underwriter_verified_income_type:
          applicationData.underwriter_verified_income_type ?? "net",
        current_employment: updatedJobs,
        enter_date: applicationData.enter_date ?? {},
      };

      // Include deleted jobs if any
      if (deletedJobIds.length > 0) {
        updateData.deleted = {
          resident_job: deletedJobIds,
        };
      }

      // Save to API - updateApplication now automatically fetches latest data
      const updatedData = await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Clear deleted job IDs after successful save
      if (deletedJobIds.length > 0) {
        setDeletedJobIds([]);
      }

      // Small delay to allow route guard to process the state update without redirecting
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Reload the saved job data from API response (Angular parity: API may return current_employment or current_job)
      const currentAfterSave =
        updatedData.current_employment || updatedData.current_job || [];
      if (currentAfterSave.length > 0) {
        // Find the saved job by matching company name and start date
        const savedJob = currentAfterSave.find(
          (job: CurrentEmployment) => {
            const jobStartDate = job.job_start_date || "";
            const employerStartDate = convertToAPIDateFormat(
              employer.jobStartDate,
            );
                return (
              job.employer_name === employer.companyName &&
              jobStartDate === employerStartDate
            );
          },
        ) as CurrentEmployment | undefined;

        if (savedJob) {
          // Get job_id
          const jobIdNum =
            typeof savedJob.job_id === "string" && savedJob.job_id !== ""
              ? parseInt(savedJob.job_id)
              : typeof savedJob.job_id === "number"
                ? savedJob.job_id
                : undefined;

          // Convert API date (YYYY-MM-DD) to display format (MM/DD/YYYY)
          let displayStartDate = "";
          if (savedJob.job_start_date) {
            if (savedJob.job_start_date.includes("/")) {
              displayStartDate = savedJob.job_start_date;
            } else {
              displayStartDate = convertToDisplayFormat(
                savedJob.job_start_date,
              );
            }
          }
          const presentJob =
            savedJob.present_job === "1" || !savedJob.job_end_date;
          let displayEndDate = "";
          if (savedJob.job_end_date) {
            if (savedJob.job_end_date.includes("/")) {
              displayEndDate = savedJob.job_end_date.split(" ")[0];
            } else {
              displayEndDate = convertToDisplayFormat(
                savedJob.job_end_date.split(" ")[0],
              );
            }
          }

          // Update the employer with saved data from API to ensure sync (Angular parity)
          setCurrentEmployers((prev) =>
            prev.map((emp) => {
              if (emp.id === employer.id) {
                return {
                  ...emp,
                  jobId: jobIdNum,
                  companyName: savedJob.employer_name || emp.companyName,
                  jobStartDate: displayStartDate || emp.jobStartDate,
                  presentJob,
                  jobEndDate: presentJob ? undefined : displayEndDate || undefined,
                  monthlyIncome: savedJob.income
                    ? parseFloat(savedJob.income).toString()
                    : emp.monthlyIncome,
                  employmentType:
                    savedJob.job_type === "FULL_TIME"
                      ? "full-time"
                      : savedJob.job_type === "PART_TIME"
                        ? "part-time"
                        : savedJob.job_type === "SELF_EMPLOYED"
                          ? "self-employed"
                          : emp.employmentType,
                  monthsAtJob: savedJob.job_duration
                    ? savedJob.job_duration.toString()
                    : emp.monthsAtJob,
                  address: savedJob.employer_street_address || emp.address,
                  city: savedJob.employer_city || emp.city,
                  state: savedJob.employer_state || emp.state,
                  zipCode: savedJob.employer_zip_code
                    ? savedJob.employer_zip_code.toString()
                    : emp.zipCode,
                  country: savedJob.employer_country || emp.country,
                  distanceExplanation:
                    savedJob.distance_explain || emp.distanceExplanation,
                  jobDistance:
                    parseJobDistanceFromApi(savedJob.job_distance) ??
                    emp.jobDistance,
                  distanceAddressSnapshot: (() => {
                    const distance =
                      parseJobDistanceFromApi(savedJob.job_distance) ??
                      emp.jobDistance;
                    if (distance === undefined) {
                      return emp.distanceAddressSnapshot;
                    }
                    return distanceAddressSnapshotForEmployer({
                      streetName:
                        savedJob.employer_street || emp.streetName,
                      city: savedJob.employer_city || emp.city,
                      state: savedJob.employer_state || emp.state,
                      zipCode: savedJob.employer_zip_code
                        ? savedJob.employer_zip_code.toString()
                        : emp.zipCode,
                      address:
                        savedJob.employer_street_address || emp.address,
                    });
                  })(),
                };
              }
              return emp;
            }),
          );
        }
      }

      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: {
          type: "success",
          text: "Current employment saved successfully!",
        },
      }));
      sendAnalytics('autosave', 'resident application info', applicationData?.applicant_id);

      // Clear message after 5 seconds
      setTimeout(() => {
        setSaveMessage((prev) => {
          const newState = { ...prev };
          delete newState[employer.id];
          return newState;
        });
      }, 5000);
    } catch (error) {
      sendAnalytics('autosave', 'resident application info error', applicationData?.applicant_id);
      const errorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to save employer. Please try again.";
      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: { type: "error", text: errorMessage },
      }));
      setValidationModalMessage(errorMessage);
      setShowValidationModal(true);
    } finally {
      setIsSaving((prev) => ({ ...prev, [employer.id]: false }));
    }
  };

  // Handle save previous employer
  const handleSavePreviousEmployer = async (employer: PreviousEmployer) => {
    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    if (!applicationData?.applicant_id) {
      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: { type: "error", text: "Application ID not found" },
      }));
      return;
    }

    // Validate fields
    const errors = validatePreviousEmployer(employer);
    if (Object.keys(errors).length > 0) {
      setPreviousEmployerErrors((prev) => ({
        ...prev,
        [employer.id]: errors,
      }));
      setValidateForm(true);
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      Object.entries(errors).forEach(([field, message]) => {
        const fieldLabel = getFieldLabel(field);
        errorMessages.push(`${fieldLabel}: ${message}`);
      });
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setShowValidationModal(true);
      }
      return;
    }

    // Clear errors if validation passes
    setPreviousEmployerErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[employer.id];
      return newErrors;
    });

    setIsSaving((prev) => ({ ...prev, [employer.id]: true }));
    // Clear any previous message while saving
    setSaveMessage((prev) => {
      const newMessages = { ...prev };
      delete newMessages[employer.id];
      return newMessages;
    });

    try {
      // Get current job start date for gap calculation (get earliest current job)
      const currentJobs = applicationData.current_employment || [];
      const earliestCurrentJob =
        currentJobs.length > 0
          ? currentJobs.reduce(
            (earliest: CurrentEmployment | null, job: CurrentEmployment) => {
              if (!earliest) return job;
              const earliestDate = earliest.job_start_date
                ? new Date(earliest.job_start_date)
                : null;
              const jobDate = job.job_start_date
                ? new Date(job.job_start_date)
                : null;
              if (!earliestDate) return job;
              if (!jobDate) return earliest;
              return jobDate < earliestDate ? job : earliest;
            },
            null,
          )
          : null;
      const currentJobStartDate = earliestCurrentJob?.job_start_date;

      // Build job data
      const jobData = buildPreviousJobData(employer, currentJobStartDate);

      // Get existing jobs (Angular parity: API may return previous_employment or previous_job)
      const previousJobsFromApi =
        applicationData.previous_employment ||
        applicationData.previous_job ||
        [];
      const existingJobs = previousJobsFromApi.filter(
        (job: CurrentEmployment) => {
          const jobIdNum =
            typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
          return jobIdNum && !deletedJobIds.includes(jobIdNum);
        },
      );

      // Update or add job
      let updatedJobs: CurrentEmployment[];
      if (employer.jobId) {
        // Update existing job - find by job_id
        updatedJobs = existingJobs.map((job: CurrentEmployment) => {
          const jobIdNum =
            typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
          return jobIdNum === employer.jobId ? jobData : job;
        });
      } else {
        // Add new job
        updatedJobs = [...existingJobs, jobData];
      }

      // Prepare update data matching Angular employmentinfo payload
      const updateData: UpdateApplicationRequest = {
        ...incomeValidationHints("section"),
        previous_employer: "1",
        previous_employment: updatedJobs,
        enter_date: applicationData.enter_date ?? {},
      };

      // Include deleted jobs if any
      if (deletedJobIds.length > 0) {
        updateData.deleted = {
          resident_job: deletedJobIds,
        };
      }

      // Save to API - updateApplication now automatically fetches latest data
      const updatedData = await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Clear deleted job IDs after successful save
      if (deletedJobIds.length > 0) {
        setDeletedJobIds([]);
      }

      // Small delay to allow route guard to process the state update without redirecting
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Reload the saved job data from API response (Angular parity: API may return previous_employment or previous_job)
      const previousAfterSave =
        updatedData.previous_employment || updatedData.previous_job || [];
      if (previousAfterSave.length > 0) {
        // Find the saved job by matching company name and start date
        const savedJob = previousAfterSave.find(
          (job: CurrentEmployment) => {
            const jobStartDate = job.job_start_date || "";
            const employerStartDate = convertToAPIDateFormat(
              employer.jobStartDate,
            );
            return (
              job.employer_name === employer.companyName &&
              jobStartDate === employerStartDate
            );
          },
        ) as CurrentEmployment | undefined;

        if (savedJob) {
          // Get job_id
          const jobIdNum =
            typeof savedJob.job_id === "string" && savedJob.job_id !== ""
              ? parseInt(savedJob.job_id)
              : typeof savedJob.job_id === "number"
                ? savedJob.job_id
                : undefined;

          // Convert API dates (YYYY-MM-DD) to display format (MM/DD/YYYY)
          let displayStartDate = "";
          if (savedJob.job_start_date) {
            if (savedJob.job_start_date.includes("/")) {
              displayStartDate = savedJob.job_start_date;
            } else {
              displayStartDate = convertToDisplayFormat(
                savedJob.job_start_date,
              );
            }
          }

          let displayEndDate = "";
          if (savedJob.job_end_date) {
            if (savedJob.job_end_date.includes("/")) {
              displayEndDate = savedJob.job_end_date;
            } else {
              displayEndDate = convertToDisplayFormat(savedJob.job_end_date);
            }
          }

          // Update the employer with saved data from API
          setPreviousEmployers((prev) =>
            prev.map((emp) => {
              if (emp.id === employer.id) {
                return {
                  ...emp,
                  jobId: jobIdNum,
                  companyName: savedJob.employer_name || emp.companyName,
                  jobStartDate: displayStartDate || emp.jobStartDate,
                  jobEndDate: displayEndDate || emp.jobEndDate,
                  monthlyIncome: savedJob.income
                    ? parseFloat(savedJob.income).toString()
                    : emp.monthlyIncome,
                  employmentType:
                    savedJob.job_type === "FULL_TIME"
                      ? "full-time"
                      : savedJob.job_type === "PART_TIME"
                        ? "part-time"
                        : savedJob.job_type === "SELF_EMPLOYED"
                          ? "self-employed"
                          : emp.employmentType,
                  monthsAtJob: savedJob.job_duration
                    ? savedJob.job_duration.toString()
                    : emp.monthsAtJob,
                  address: savedJob.employer_street_address || emp.address,
                  city: savedJob.employer_city || emp.city,
                  state: savedJob.employer_state || emp.state,
                  zipCode: savedJob.employer_zip_code
                    ? savedJob.employer_zip_code.toString()
                    : emp.zipCode,
                  country: savedJob.employer_country || emp.country,
                  jobGap: savedJob.job_gap || emp.jobGap,
                  jobGapExplanation:
                    savedJob.job_gap_explanation || emp.jobGapExplanation,
                  distanceExplanation:
                    savedJob.distance_explain || emp.distanceExplanation,
                  jobDistance:
                    typeof savedJob.job_distance === "number"
                      ? savedJob.job_distance
                      : (typeof savedJob.job_distance === "string" &&
                        savedJob.job_distance !== ""
                        ? parseFloat(savedJob.job_distance)
                        : undefined) || emp.jobDistance,
                };
              }
              return emp;
            }),
          );
        }
      }

      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: {
          type: "success",
          text: "Previous employment saved successfully!",
        },
      }));
      sendAnalytics('autosave', 'resident application info', applicationData?.applicant_id);

      // Clear message after 5 seconds
      setTimeout(() => {
        setSaveMessage((prev) => {
          const newState = { ...prev };
          delete newState[employer.id];
          return newState;
        });
      }, 5000);
    } catch (error) {
      sendAnalytics('autosave', 'resident application info error', applicationData?.applicant_id);
      const errorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to save previous employer. Please try again.";
      setSaveMessage((prev) => ({
        ...prev,
        [employer.id]: { type: "error", text: errorMessage },
      }));
      setValidationModalMessage(errorMessage);
      setShowValidationModal(true);
    } finally {
      setIsSaving((prev) => ({ ...prev, [employer.id]: false }));
    }
  };

  const addCurrentEmployer = () => {
    const newEmployer: CurrentEmployer = {
      id: `current-${Date.now()}`,
      employmentType: "full-time", // Default to Full Time
      companyName: "",
      address: "",
      otherOutsideUS: false,
      monthlyIncome: "",
      yearlyCalculatedIncome: "",
      jobStartDate: "",
      presentJob: true, // Default to "Current Active" checked
      monthsAtJob: "",
      hrManagerEmail: "",
      website: "",
      jobDistance: undefined,
      applicantToEmployerDistance: undefined,
      distanceExplanation: undefined,
    };
    setCurrentEmployers([...currentEmployers, newEmployer]);
  };

  const getPropertyAddress = (): string => {
    return resolvePropertyAddressFromApplication(
      applicationData as Record<string, unknown>,
    );
  };

  const getApplicantAddress = (): string => {
    return resolveApplicantAddressFromApplication(
      applicationData as Record<string, unknown>,
    );
  };

  const currentEmployerRequiresDistanceWarning = (
    employer: CurrentEmployer,
  ): boolean =>
    anyDistanceRequiresExplanation(
      employer.jobDistance,
      applicantToPropertyDistanceRef.current,
      employer.applicantToEmployerDistance,
    );

  const applyCalculatedDistances = (
    employer: CurrentEmployer,
    distances: EmploymentDistanceCheckResult,
  ): CurrentEmployer => {
    const merged = mergeCalculatedEmploymentDistances(
      {
        propertyToEmployer: employer.jobDistance,
        applicantToEmployer: employer.applicantToEmployerDistance,
        applicantToProperty: applicantToPropertyDistanceRef.current,
      },
      distances,
    );

    return {
      ...employer,
      jobDistance: merged.propertyToEmployer,
      applicantToEmployerDistance: merged.applicantToEmployer,
      distanceAddressSnapshot: distanceAddressSnapshotForEmployer(employer),
    };
  };

  const updateApplicantToPropertyDistance = (
    distances: EmploymentDistanceCheckResult,
    propertyAddress: string,
    applicantAddress: string,
  ) => {
    const nextDistance =
      propertyAddress.trim() && applicantAddress.trim()
        ? distances.applicantToProperty ??
          applicantToPropertyDistanceRef.current
        : undefined;
    applicantToPropertyDistanceRef.current = nextDistance;
    setApplicantToPropertyDistance(nextDistance);
  };

  /** Recalculate driving distance (with straight-line fallback) before save. */
  const refreshEmployerDistanceBeforeSave = async (
    employer: CurrentEmployer,
  ): Promise<CurrentEmployer> => {
    const propertyAddress = getPropertyAddress();
    const applicantAddress = getApplicantAddress();
    const employerAddress = buildEmployerAddressString(employer);

    if (!employerAddress) {
      return employer;
    }

    setCalculatingDistance(employer.id);
    try {
      const distances = await recalculateAllEmploymentDistances(
        propertyAddress,
        applicantAddress,
        employerAddress,
      );
      updateApplicantToPropertyDistance(
        distances,
        propertyAddress,
        applicantAddress,
      );
      if (
        distances.propertyToEmployer !== null ||
        distances.applicantToEmployer !== null
      ) {
        return applyCalculatedDistances(employer, distances);
      }
    } finally {
      setCalculatingDistance(null);
    }

    return employer;
  };

  const refreshAllCurrentEmployerDistancesBeforeSave = async (
    employers: CurrentEmployer[],
  ): Promise<CurrentEmployer[]> => {
    if (employers.length === 0) {
      return employers;
    }

    const employersNeedingRecall = employers.filter((employer) =>
      needsEmploymentDistanceRecall(employer),
    );
    if (employersNeedingRecall.length === 0) {
      return employers;
    }

    const propertyAddress = getPropertyAddress();
    const applicantAddress = getApplicantAddress();

    setIsRecalculatingDistances(true);
    try {
      return await Promise.all(
        employers.map(async (employer) => {
          if (!needsEmploymentDistanceRecall(employer)) {
            return employer;
          }

          const employerAddress = buildEmployerAddressString(employer);
          if (!employerAddress) {
            return employer;
          }

          const distances = await recalculateAllEmploymentDistances(
            propertyAddress,
            applicantAddress,
            employerAddress,
          );
          updateApplicantToPropertyDistance(
            distances,
            propertyAddress,
            applicantAddress,
          );
          if (
            distances.propertyToEmployer !== null ||
            distances.applicantToEmployer !== null
          ) {
            return applyCalculatedDistances(employer, distances);
          }
          return employer;
        }),
      );
    } finally {
      setIsRecalculatingDistances(false);
    }
  };

  const refreshAllEmploymentDistanceWarnings = async (
    employers: CurrentEmployer[],
  ): Promise<void> => {
    if (employers.length === 0) {
      applicantToPropertyDistanceRef.current = undefined;
      setApplicantToPropertyDistance(undefined);
      return;
    }

    const propertyAddress = getPropertyAddress();
    const applicantAddress = getApplicantAddress();
    if (!propertyAddress && !applicantAddress) {
      return;
    }

    setIsRecalculatingDistances(true);
    try {
      const updatedEmployers = await Promise.all(
        employers.map(async (employer) => {
          const employerAddress = buildEmployerAddressString(employer);
          if (!employerAddress) {
            return employer;
          }

          const distances = await recalculateAllEmploymentDistances(
            propertyAddress,
            applicantAddress,
            employerAddress,
          );
          updateApplicantToPropertyDistance(
            distances,
            propertyAddress,
            applicantAddress,
          );
          return applyCalculatedDistances(employer, distances);
        }),
      );
      const refreshedById = new Map(
        updatedEmployers.map((employer) => [employer.id, employer]),
      );
      setCurrentEmployers((current) =>
        current.map((employer) => {
          const refreshed = refreshedById.get(employer.id);
          return refreshed
            ? {
                ...employer,
                jobDistance: refreshed.jobDistance,
                applicantToEmployerDistance:
                  refreshed.applicantToEmployerDistance,
                distanceAddressSnapshot: refreshed.distanceAddressSnapshot,
              }
            : employer;
        }),
      );
    } finally {
      setIsRecalculatingDistances(false);
    }
  };

  /**
   * Angular parity: validateDistance + distanceCalculator
   * Recalculates distance when employer address fields change via manual entry (blur on city/state/zip).
   * Angular triggers this on focusout of employer_city, employer_state, employer_zip_code.
   */
  const validateDistance = async (employerId: string, fields: EmployerAddressFields) => {
    if (!fields.streetName || !fields.city || !fields.state || !fields.zipCode) {
      return;
    }

    const employerAddress = `${fields.streetName} ${fields.city} ${fields.state} ${fields.zipCode}`;
    const propertyAddress = getPropertyAddress();
    const applicantAddress = getApplicantAddress();

    if (!propertyAddress && !applicantAddress) {
      return;
    }

    setCalculatingDistance(employerId);
    try {
      const distances = await recalculateAllEmploymentDistances(
        propertyAddress,
        applicantAddress,
        employerAddress,
      );
      updateApplicantToPropertyDistance(
        distances,
        propertyAddress,
        applicantAddress,
      );
      setCurrentEmployers((prev) =>
        prev.map((emp) =>
          emp.id === employerId
            ? applyCalculatedDistances(emp, distances)
            : emp,
        ),
      );
    } catch {
      // ZERO_RESULTS or other API errors — matches Angular behavior (silently ignores)
    } finally {
      setCalculatingDistance(null);
    }
  };

  /**
   * Handle employer address selection from Google Places
   */
  const handleEmployerAddressSelect = async (
    employerId: string,
    place: PlaceResult,
  ) => {
    const fullAddress = place.formatted_address || "";

    // Parse address components
    const parsed = parseAddressComponents(place);

    // Street = street number + street name (match Angular: "2356 Sutter Street")
    const streetLine = [parsed.streetNumber, parsed.streetName].filter(Boolean).join(" ").trim() || (parsed.streetName || "");

    // Update employer address and all address fields
    updateCurrentEmployer(employerId, "address", fullAddress);
    updateCurrentEmployer(employerId, "streetName", streetLine);
    updateCurrentEmployer(employerId, "aptUnit", parsed.aptUnit || "");
    updateCurrentEmployer(employerId, "city", parsed.city);
    updateCurrentEmployer(employerId, "state", parsed.state);
    updateCurrentEmployer(employerId, "zipCode", parsed.zipCode);
    updateCurrentEmployer(employerId, "country", parsed.country);

    // Determine if outside US
    const isOutsideUS = parsed.country && parsed.country.toUpperCase() !== "US";
    updateCurrentEmployer(employerId, "otherOutsideUS", isOutsideUS);

    const propertyAddress = getPropertyAddress();
    const applicantAddress = getApplicantAddress();

    if ((propertyAddress || applicantAddress) && fullAddress) {
      setCalculatingDistance(employerId);
      try {
        const distances = await recalculateAllEmploymentDistances(
          propertyAddress,
          applicantAddress,
          fullAddress,
        );
        updateApplicantToPropertyDistance(
          distances,
          propertyAddress,
          applicantAddress,
        );
        setCurrentEmployers((prev) =>
          prev.map((emp) =>
            emp.id === employerId
              ? applyCalculatedDistances(emp, distances)
              : emp,
          ),
        );
      } catch {
        // ZERO_RESULTS or other API errors — matches Angular behavior (silently ignores)
      } finally {
        setCalculatingDistance(null);
      }
    }
  };

  // Re-evaluate all distance warnings when applicant/property addresses change (About tab) or employers load.
  useEffect(() => {
    const employers = currentEmployersRef.current;
    if (employmentStatus !== "employed" || employers.length === 0) {
      return;
    }

    const hasEmployerAddress = employers.some(
      (employer) => buildEmployerAddressString(employer).length > 0,
    );
    if (!hasEmployerAddress) {
      return;
    }

    void refreshAllEmploymentDistanceWarnings(employers);
  }, [
    employmentStatus,
    currentEmployers.length,
    applicationData?.current_address,
    applicationData?.property,
    applicationData?.current_employment,
    applicationData?.current_job,
  ]);

  const removeCurrentEmployer = (id: string) => {
    const employer = currentEmployers.find((emp) => emp.id === id);
    // If employer has a jobId, track it for deletion
    if (employer?.jobId) {
      setDeletedJobIds((prev) => [...prev, employer.jobId!]);
    }
    const filtered = currentEmployers.filter((emp) => emp.id !== id);
    setCurrentEmployers(filtered);
    // Angular parity: when cancelling and no current employers remain, set status to Not Employed
    if (filtered.length === 0) {
      setEmploymentStatus("not-employed");
    }
  };

  const updateCurrentEmployer = (
    id: string,
    field: keyof CurrentEmployer,
    value: string | boolean | number | undefined,
  ) => {
    if (field === "companyName" && typeof value === "string") {
      const isDuplicate = currentEmployers.some(
        (emp) =>
          emp.id !== id &&
          emp.companyName.trim().toLowerCase() === value.trim().toLowerCase(),
      );

      if (isDuplicate) {
        setCurrentEmployerErrors((prev) => ({
          ...prev,
          [id]: { ...prev[id], companyName: "This employer already exists." },
        }));
      } else {
        if (currentEmployerErrors[id]?.companyName) {
          setCurrentEmployerErrors((prev) => {
            const newErrors = { ...prev[id] };
            delete newErrors.companyName;
            if (Object.keys(newErrors).length === 0) {
              const next = { ...prev };
              delete next[id];
              return next;
            }
            return { ...prev, [id]: newErrors };
          });
        }
      }
    }

    setCurrentEmployers((prev) =>
      prev.map((emp) => (emp.id === id ? { ...emp, [field]: value } : emp)),
    );
  };

  const addPreviousEmployer = () => {
    const newEmployer: PreviousEmployer = {
      id: `previous-${Date.now()}`,
      employmentType: "",
      companyName: "",
      address: "",
      otherOutsideUS: false,
      monthlyIncome: "",
      yearlyCalculatedIncome: "",
      jobStartDate: "",
      jobEndDate: "",
      monthsAtJob: "",
    };
    setPreviousEmployers([...previousEmployers, newEmployer]);
  };

  const removePreviousEmployer = (id: string) => {
    const employer = previousEmployers.find((emp) => emp.id === id);
    // If employer has a jobId, track it for deletion
    if (employer?.jobId) {
      setDeletedJobIds((prev) => [...prev, employer.jobId!]);
    }
    const filtered = previousEmployers.filter((emp) => emp.id !== id);
    setPreviousEmployers(filtered);
    // Angular parity: when cancelling and no previous employers remain, set to "Not Employed" (no previous employer)
    if (filtered.length === 0) {
      setHasPreviousEmployer(false);
    }
  };

  const updatePreviousEmployer = (
    id: string,
    field: keyof PreviousEmployer,
    value: string | boolean | number | undefined,
  ) => {
    setPreviousEmployers((prev) =>
      prev.map((emp) => (emp.id === id ? { ...emp, [field]: value } : emp)),
    );
  };

  const addOtherIncome = () => {
    // If there are existing incomes, validate the last one before adding new
    if (otherIncomes.length > 0) {
      const lastIncome = otherIncomes[otherIncomes.length - 1];
      const errors = validateOtherIncome(lastIncome);

      if (Object.keys(errors).length > 0) {
        // Show errors for the last income
        setOtherIncomeErrors((prev) => ({
          ...prev,
          [lastIncome.id]: errors,
        }));
        setSaveOtherIncomeMessage((prev) => ({
          ...prev,
          [lastIncome.id]: {
            type: "error",
            text: "Please fix the validation errors before adding another income.",
          },
        }));
        return; // Don't add new income if validation fails
      }

      // Save the last income to the array (it's already in state, just ensure it's validated)
      // The income is already in the array, we just need to clear errors
      setOtherIncomeErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[lastIncome.id];
        return newErrors;
      });
    }

    // Add new empty income form
    const newIncome: OtherIncome = {
      id: `income-${Date.now()}`,
      incomeFrequency: "monthly", // Default to monthly (Per_Month)
      sourceName: "",
      income: "",
      yearlyCalculatedIncome: "",
      startDate: "",
      endDate: "",
      currentlyActive: false, // Default to unchecked
      monthsOfIncome: "",
      transactionDate: "",
      amount: "",
    };
    setOtherIncomes((prev) => [...prev, newIncome]);
  };

  const removeOtherIncome = (id: string) => {
    // If income has a jobId (saved item), track it for deletion
    // ID format: "other-{jobId}" for saved items
    const jobIdMatch = id.match(/^other-(\d+)$/);
    if (jobIdMatch) {
      const jobId = parseInt(jobIdMatch[1]);
      setDeletedJobIds((prev) => [...prev, jobId]);
    }
    setOtherIncomes(otherIncomes.filter((income) => income.id !== id));
  };

  const updateOtherIncome = (
    id: string,
    field: keyof OtherIncome,
    value: string | boolean,
  ) => {
    setOtherIncomes((prev) =>
      prev.map((income) => {
        if (income.id !== id) return income;

        const updatedIncome = { ...income, [field]: value };

        // Auto-calculate months when startDate, endDate, or currentlyActive changes
        if (
          field === "startDate" ||
          field === "endDate" ||
          field === "currentlyActive"
        ) {
          if (updatedIncome.startDate) {
            if (updatedIncome.currentlyActive) {
              // Match Angular: months = moment(end).diff(start, 'months') + 1
              const months = employmentMonthsAtJob(updatedIncome.startDate);
              updatedIncome.monthsOfIncome = months.toString();
            } else if (updatedIncome.endDate) {
              const months = employmentMonthsAtJob(
                updatedIncome.startDate,
                updatedIncome.endDate,
              );
              updatedIncome.monthsOfIncome = months.toString();
            } else {
              // No end date and not currently active, clear months
              updatedIncome.monthsOfIncome = "";
            }
          } else {
            // No start date, clear months
            updatedIncome.monthsOfIncome = "";
          }
        }

        return updatedIncome;
      }),
    );
    // Clear error for this field when user starts typing
    if (otherIncomeErrors[id]?.[field]) {
      setOtherIncomeErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[id]) {
          const incomeErrors = { ...newErrors[id] };
          delete incomeErrors[field];
          if (Object.keys(incomeErrors).length === 0) {
            delete newErrors[id];
          } else {
            newErrors[id] = incomeErrors;
          }
        }
        return newErrors;
      });
    }
  };

  // Validate other income
  const validateOtherIncome = (income: OtherIncome): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!income.incomeFrequency) {
      errors.incomeFrequency = APPLICANT_CONTENT.errors.employment.incomeFrequency;
      return errors; // Return early if frequency not selected
    }

    if (!income.sourceName || income.sourceName.trim() === "") {
      errors.sourceName = APPLICANT_CONTENT.errors.employment.sourceName;
    }

    // Validate based on frequency type
    if (income.incomeFrequency === "one-time") {
      // One-time validation: Amount and Transaction Date
      const amount = income.amount || income.income;
      if (!amount || amount.trim() === "") {
        errors.amount = APPLICANT_CONTENT.errors.employment.amount;
        errors.income = APPLICANT_CONTENT.errors.employment.amount;
      }

      if (!income.transactionDate || income.transactionDate.trim() === "") {
        errors.transactionDate = APPLICANT_CONTENT.errors.employment.transactionDate;
      }

      // Validate transaction date is not in the future
      if (income.transactionDate && income.transactionDate.trim() !== "") {
        // Convert MM/DD/YYYY to Date object
        const dateStr = income.transactionDate;
        let transactionDate: Date;
        if (dateStr.includes("/")) {
          // MM/DD/YYYY format
          const [month, day, year] = dateStr.split("/").map(Number);
          transactionDate = new Date(year, month - 1, day);
        } else {
          // Try parsing as ISO or other format
          transactionDate = new Date(dateStr);
        }

        if (!isNaN(transactionDate.getTime())) {
          const today = new Date();
          today.setHours(23, 59, 59, 999); // End of today
          transactionDate.setHours(23, 59, 59, 999);
          if (transactionDate > today) {
            errors.transactionDate = "Date should not be in the Future.";
          }
        }
      }
    } else {
      // Period-based validation (monthly/weekly/bi-monthly/yearly): Income, Start Date, Months
      if (!income.income || income.income.trim() === "") {
        errors.income = APPLICANT_CONTENT.errors.employment.income;
      }

      if (!income.startDate || income.startDate.trim() === "") {
        errors.startDate = APPLICANT_CONTENT.errors.employment.startDate;
      } else {
        // Validate start date is not in the future
        const startDate = new Date(income.startDate);
        const today = new Date();
        today.setHours(23, 59, 59, 999); // End of today
        if (startDate > today) {
          errors.startDate = "'Date should not be in the Future.'";
        }
      }

      // End date validation: required if not currently active
      if (!income.currentlyActive) {
        if (!income.endDate || income.endDate.trim() === "") {
          errors.endDate = APPLICANT_CONTENT.errors.employment.endDate;
        } else {
          // Validate end date is not before start date
          if (income.startDate && income.startDate.trim() !== "") {
            const startDate = new Date(income.startDate);
            const endDate = new Date(income.endDate);
            if (endDate < startDate) {
              errors.endDate = "'End Date' cannot be before 'Start Date'.";
            }
          }
          // Validate end date is not in the future
          const endDate = new Date(income.endDate);
          const today = new Date();
          today.setHours(23, 59, 59, 999); // End of today
          if (endDate > today) {
            errors.endDate = "'Date should not be in the Future.'";
          }
        }
      }

      if (!income.monthsOfIncome || income.monthsOfIncome.trim() === "") {
        errors.monthsOfIncome = APPLICANT_CONTENT.errors.employment.monthsOfIncome;
      }
    }

    return errors;
  };

  // Map income frequency to API income_period format
  const mapIncomeFrequencyToPeriod = (frequency: string): string => {
    switch (frequency) {
      case "monthly":
        return "Per_Month";
      case "one-time":
        return "One_Time";
      // Reverted: income period options only include monthly and one-time
      default:
        return "Per_Month";
    }
  };

  // Build other income data for API (using other_source structure)
  const buildOtherIncomeData = (
    income: OtherIncome,
    jobId?: number,
    index?: number,
  ): { otherSource: OtherSource; enterDate: Record<string, string> } => {
    // For one-time, use amount; for monthly, use income
    const amountValue =
      income.incomeFrequency === "one-time"
        ? income.amount || income.income
        : income.income;
    const incomeNum = parseFloat(getNumericValue(amountValue)) || 0;
    const monthsOfIncome = parseInt(income.monthsOfIncome) || 0;

    // For one-time, use transactionDate; for monthly, use startDate/endDate
    const startDate =
      income.incomeFrequency === "one-time"
        ? income.transactionDate
          ? convertToAPIDateFormat(income.transactionDate)
          : undefined
        : income.startDate
          ? convertToAPIDateFormat(income.startDate)
          : undefined;

    const endDate =
      income.incomeFrequency === "one-time"
        ? income.transactionDate
          ? convertToAPIDateFormat(income.transactionDate)
          : null // Same as start for one-time
        : income.currentlyActive
          ? null
          : income.endDate
            ? convertToAPIDateFormat(income.endDate)
            : null;

    // present_job should be "1" for active, "0" for inactive (API expects string)
    const presentJob =
      income.incomeFrequency === "one-time"
        ? "0" // One-time is always not active (single transaction)
        : income.currentlyActive
          ? "1"
          : "0";

    // Angular parity: other_source always sends job_duration; use "0" for one-time or zero months (required by API)
    const jobDuration =
      income.incomeFrequency === "one-time"
        ? "0"
        : monthsOfIncome > 0
          ? String(monthsOfIncome)
          : "0";

    const otherSource = {
      job_id: jobId ? jobId : "", // Empty string for new, number for existing
      source_name: income.sourceName,
      income: incomeNum > 0 ? incomeNum.toString() : undefined,
      income_period: mapIncomeFrequencyToPeriod(income.incomeFrequency),
      job_start_date: startDate,
      job_end_date: endDate,
      present_job: presentJob, // "1" or "0" (API string)
      job_duration: jobDuration,
      check_types: "",
      reference_contact: null,
      other_proof_of_income: "",
    };

    // Build enter_date object with date strings in MM/DD/YYYY format
    const enterDate: Record<string, string> = {};
    if (income.incomeFrequency === "one-time") {
      // For one-time, use transactionDate
      if (income.transactionDate && index !== undefined) {
        enterDate[`job_start_date_other_source_${index}`] =
          income.transactionDate; // Already in MM/DD/YYYY format
        enterDate[`job_end_date_other_source_${index}`] =
          income.transactionDate; // Same date for one-time
      }
    } else {
      // For monthly, use startDate and endDate
      if (income.startDate && index !== undefined) {
        enterDate[`job_start_date_other_source_${index}`] = income.startDate; // Already in MM/DD/YYYY format
      }
      if (income.endDate && index !== undefined) {
        enterDate[`job_end_date_other_source_${index}`] = income.endDate; // Already in MM/DD/YYYY format
      }
    }

    return { otherSource: otherSource as unknown as OtherSource, enterDate };
  };

  // Handle save other income
  const handleSaveOtherIncome = async (income: OtherIncome) => {
    // Prevent API calls when application is read-only
    if (isReadOnly) {
      return;
    }

    if (!applicationData?.applicant_id) {
      setSaveOtherIncomeMessage((prev) => ({
        ...prev,
        [income.id]: {
          type: "error",
          text: "Application ID not found. Please refresh the page.",
        },
      }));
      return;
    }

    // Validate
    const errors = validateOtherIncome(income);

    if (Object.keys(errors).length > 0) {
      setOtherIncomeErrors((prev) => ({
        ...prev,
        [income.id]: errors,
      }));
      // Show validation popup with all errors
      const errorMessages: string[] = [];
      Object.entries(errors).forEach(([field, message]) => {
        const fieldLabel = getFieldLabel(field);
        errorMessages.push(`${fieldLabel}: ${message}`);
      });
      if (errorMessages.length > 0) {
        setValidationModalMessage(errorMessages.join("\n"));
        setShowValidationModal(true);
      }
      return;
    }

    // Clear errors
    setOtherIncomeErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[income.id];
      return newErrors;
    });

    setIsSavingOtherIncome((prev) => ({ ...prev, [income.id]: true }));
    // Clear previous message while saving; success message only after API succeeds
    setSaveOtherIncomeMessage((prev) => {
      const next = { ...prev };
      delete next[income.id];
      return next;
    });

    try {
      // Get existing other sources (Angular parity: API may return other_source or resident_other_source)
      const otherSourceFromApi = getResidentOtherSources(applicationData);
      const existingOtherSources = otherSourceFromApi.filter(
        (os: OtherSource) => {
          const osJobId =
            typeof os.job_id === "string" ? parseInt(os.job_id) : os.job_id;
          return osJobId && !deletedJobIds.includes(osJobId);
        },
      );

      // Check if this is an update (income has a jobId stored in the id)
      // ID format: "other-{jobId}" for saved items, or random string for new items
      const jobIdMatch = income.id.match(/^other-(\d+)$/);
      const existingJobId = jobIdMatch ? parseInt(jobIdMatch[1]) : undefined;

      // Find the index of this income in the array for enter_date keys
      const incomeIndex =
        existingJobId !== undefined
          ? existingOtherSources.findIndex((os: OtherSource) => {
            const osJobId =
              typeof os.job_id === "string" ? parseInt(os.job_id) : os.job_id;
            return osJobId === existingJobId;
          })
          : -1;
      const finalIndex =
        incomeIndex >= 0 ? incomeIndex : existingOtherSources.length;

      // Build other income data for API
      const { otherSource, enterDate } = buildOtherIncomeData(
        income,
        existingJobId,
        finalIndex,
      );

      let updatedOtherSources: OtherSource[];
      if (existingJobId !== undefined && incomeIndex >= 0) {
        // Update existing other income source
        updatedOtherSources = existingOtherSources.map((os: OtherSource) => {
          const osJobId =
            typeof os.job_id === "string" ? parseInt(os.job_id) : os.job_id;
          if (osJobId === existingJobId) {
            return otherSource;
          }
          return os;
        });
      } else {
        // Add new other income source
        updatedOtherSources = [...existingOtherSources, otherSource];
      }

      // Get existing regular jobs (Angular parity: API may return current_employment or current_job)
      const regularJobs =
        applicationData.current_employment || applicationData.current_job || [];

      // Build enter_date object - merge with existing enter_date if any
      const mergedEnterDate: Record<string, string> = {
        ...(applicationData.enter_date || {}),
      };
      Object.assign(mergedEnterDate, enterDate);

      // Prepare update data matching API structure
      const updateData: UpdateApplicationRequest = {
        ...incomeValidationHints("section"),
        current_employment: regularJobs, // Only regular employment, not other sources
        other_source: updatedOtherSources, // Other sources of income
        employment_status:
          regularJobs.length > 0
            ? "1"
            : updatedOtherSources.length > 0
              ? "1"
              : applicationData.employment_status || "0",
        previous_employer: applicationData.previous_employer || "0",
        ninja_reason: applicationData.ninja_reason || "",
        underwriter_verified_income_other:
          updatedOtherSources.length > 0 ? "1" : "0",
        underwriter_verified_income_type:
          applicationData.underwriter_verified_income_type || "net",
        enter_date:
          Object.keys(mergedEnterDate).length > 0 ? mergedEnterDate : {}, // Include enter_date with date strings
      };

      // Include deleted jobs if any
      if (deletedJobIds.length > 0) {
        updateData.deleted = {
          resident_job: deletedJobIds,
        };
      }

      // Save to API - updateApplication now automatically fetches latest data
      const updatedData = await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Clear deleted job IDs after successful save
      if (deletedJobIds.length > 0) {
        setDeletedJobIds([]);
      }

      // Small delay to allow route guard to process the state update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Update the income ID with the job_id from API response (from other_source array)
      const savedOtherIncome = updatedData.other_source?.find(
        (os: OtherSource) =>
          os.source_name === income.sourceName &&
          (existingJobId
            ? (typeof os.job_id === "string"
              ? parseInt(os.job_id)
              : os.job_id) === existingJobId
            : true),
      );

      if (savedOtherIncome) {
        const savedJobId =
          typeof savedOtherIncome.job_id === "string"
            ? parseInt(savedOtherIncome.job_id)
            : savedOtherIncome.job_id;
        if (savedJobId) {
          // Update the income ID to include job_id for future updates
          setOtherIncomes((prev) =>
            prev.map((inc) =>
              inc.id === income.id
                ? { ...inc, id: `other-${savedJobId}` }
                : inc,
            ),
          );
        }
      }

      setSaveOtherIncomeMessage((prev) => ({
        ...prev,
        [income.id]: {
          type: "success",
          text: "Other sources of income saved successfully!",
        },
      }));

      // Clear message after 5 seconds (match Current Employer section)
      setTimeout(() => {
        setSaveOtherIncomeMessage((prev) => {
          const newState = { ...prev };
          delete newState[income.id];
          return newState;
        });
      }, 5000);
    } catch (error) {
      const errorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Failed to save other income. Please try again.";
      setSaveOtherIncomeMessage((prev) => ({
        ...prev,
        [income.id]: { type: "error", text: errorMessage },
      }));
      setValidationModalMessage(errorMessage);
      setShowValidationModal(true);
    } finally {
      setIsSavingOtherIncome((prev) => ({ ...prev, [income.id]: false }));
    }
  };

  // Alias for backward compatibility with existing code
  const getNumericValue = getNumericCurrencyValue;

  // Calculate yearly income based on frequency
  const calculateYearlyIncome = (income: string, frequency: string) => {
    const amount = parseFloat(getNumericValue(income));
    if (isNaN(amount)) return "";
    const fmt = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    switch (frequency) {
      case "monthly":
        return fmt(amount * 12);
      case "one-time":
        return fmt(amount);
      // Reverted: income period options only include monthly and one-time
      default:
        return "";
    }
  };

  // Handle Link My Bank button click
  const handleLinkMyBank = async () => {
    if (!applicationData?.applicant_id) {
      setBankMessage({
        type: "error",
        text: "Application ID not found. Please refresh the page.",
      });
      setTimeout(() => setBankMessage(null), 5000);
      return;
    }

    const applicantId = applicationData.applicant_id;

    const openNinjaIframeFlow = async () => {
      setBankMessage(null);
      setIsGeneratingToken(true);
      try {
        // Angular flow requires token first (create_link_token), then ibv_request.
        const token = await createNinjaLinkToken(applicantId);
        setNinjaLastToken(token);
        tokenApplicantIdRef.current = applicantId;

        const ibvResponse = await createNinjaIbvRequest(applicantId);
        if (!ibvResponse?.ibv_connection_link) {
          throw new Error("Unable to open bank link. Please try again.");
        }
        setNinjaIbvResponseData(ibvResponse);
        setShowNinjaIframePopup(true);
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Unable to open bank link. Please try again.";
        setBankMessage({ type: "error", text: errorMessage });
        setTimeout(() => setBankMessage(null), 5000);
      } finally {
        setIsGeneratingToken(false);
      }
    };

    // Check if Ninja is required
    if (bankLinking.ninjaConfig?.NINJA_REQUIRED === "YES") {
      await openNinjaIframeFlow();
      return;
    }

    if (bankLinking.ninjaConfig) {
        await openNinjaIframeFlow();
      } else {
        setBankMessage({
          type: "error",
          text: "Bank linking is not available. Please contact support.",
        });
        setTimeout(() => setBankMessage(null), 5000);
      }
  };

  const handleUseDocumentUploadsFromModal = () => {
    setShowLinkBankModal(false);
    const noNinjaFilesYet =
      !Array.isArray(applicationData?.ninja_income_proof) ||
      applicationData.ninja_income_proof.length === 0;
    if (noNinjaFilesYet) {
      setShowBankDetailsModal(true);
    } else {
      setChoseDocumentUploads(true);
    }
  };

  const handleLinkBankFromModal = () => {
    setShowLinkBankModal(false);
    handleLinkMyBank();
  };

  /** POST /api/no_bank_ninja — used when applicant is not AWAITING (document-upload path). */
  const performNoBankNinjaUpload = async (
    bankName: string,
    fileOrFiles: File | File[],
  ) => {
    if (!applicationData?.applicant_id) {
      setBankMessage({
        type: "error",
        text: "Application ID not found. Please refresh the page.",
      });
      setTimeout(() => setBankMessage(null), 5000);
      throw new Error("no_applicant");
    }

    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const invalidType = files.find((f) => !isAcceptedIncomeOrIdDocumentFile(f));
    if (invalidType) {
      setBankMessage({
        type: "error",
        text: ACCEPTED_INCOME_AND_ID_DOCUMENT_ERROR,
      });
      setTimeout(() => setBankMessage(null), 5000);
      throw new Error("invalid_type");
    }
    const existingCount = Array.isArray(applicationData?.ninja_income_proof)
      ? applicationData.ninja_income_proof.length
      : 0;

    const maxNew = Math.max(0, 6 - existingCount);
    if (files.length > maxNew) {
      setShowSixFileUploadLimitModal(true);
      return;
    }
    if (files.length === 0 || maxNew === 0) {
      return;
    }

    const existingNames = new Set(
      (applicationData?.ninja_income_proof || []).map((f: ApplicationFileAttachment) =>
        getAttachmentFileName(f).toLowerCase(),
      ),
    );
    const newNames = new Set<string>();
    for (const file of files) {
      const name = file.name.toLowerCase();
      if (existingNames.has(name) || newNames.has(name)) {
        setBankMessage({
          type: "error",
          text: "Sorry, you can not upload the same document more than once!",
        });
        setTimeout(() => setBankMessage(null), 5000);
        throw new Error("duplicate");
      }
      newNames.add(name);
    }

    setBankMessage(null);

    await bankLinking.uploadBankDocuments(
      bankName,
      files.length === 1 ? files[0] : files,
    );

    const updated = await applicationService.getResidentApplicationById(
      applicationData.applicant_id,
    );
    dispatch(mergeBankDetailsFromResponse(updated));

    setBankMessage({
      type: "success",
      text: "Bank Details Added Successfully",
    });
    setChoseDocumentUploads(false);
    setTimeout(() => setBankMessage(null), 5000);
  };

  const handleBankDetailsModalSubmit = async (
    bankName: string,
    fileOrFiles: File | File[],
  ) => {
    await performNoBankNinjaUpload(bankName, fileOrFiles);
    setShowBankDetailsModal(false);
  };

  const handleInlineNinjaSupportingUpload = async (selectedFiles: File[]) => {
    const bankName = applicationData?.bank_name?.trim() || "";
    await performNoBankNinjaUpload(
      bankName,
      selectedFiles.length === 1 ? selectedFiles[0] : selectedFiles,
    );
  };

  // Build the income tab's API payload from current form state. Pure (no setState/nav/
  // dialogs); single source of truth for Save & Continue and auto-save. `employersForSave`
  // lets the Save handler pass distance-recalculated employers; other callers pass as-is.
  const buildIncomeUpdateData = (
    employersForSave: CurrentEmployer[],
  ): UpdateApplicationRequest => {
    const currentJobs: CurrentEmployment[] = employersForSave.map(
      (employer) => buildJobData(employer),
    );

    // Earliest current start drives the previous-job gap calc.
    const earliestCurrentJobStartDate =
      employersForSave.length > 0 && employersForSave[0].jobStartDate
        ? employersForSave[0].jobStartDate
        : undefined;
    const previousJobs: CurrentEmployment[] = previousEmployers.map(
      (employer) =>
        buildPreviousJobData(employer, earliestCurrentJobStartDate),
    );

    // Angular parity: the API may return other_source or resident_other_source.
    const otherSourceFromApi = getResidentOtherSources(applicationData);
    const existingOtherSources = otherSourceFromApi.filter((os: OtherSource) => {
      const osJobId =
        typeof os.job_id === "string" ? parseInt(os.job_id) : os.job_id;
      return osJobId && !deletedJobIds.includes(osJobId);
    });

    const mergedEnterDate: Record<string, string> = {
      ...(applicationData?.enter_date || {}),
    };

    const updatedOtherSources: OtherSource[] = [];
    otherIncomes.forEach((income) => {
      const jobIdMatch = income.id.match(/^other-(\d+)$/);
      const existingJobId = jobIdMatch ? parseInt(jobIdMatch[1]) : undefined;
      const incomeIndex =
        existingJobId !== undefined
          ? existingOtherSources.findIndex((os: OtherSource) => {
            const osJobId =
              typeof os.job_id === "string"
                ? parseInt(os.job_id)
                : os.job_id;
            return osJobId === existingJobId;
          })
          : -1;
      const finalIndex =
        incomeIndex >= 0
          ? incomeIndex
          : existingOtherSources.length + updatedOtherSources.length;

      const { otherSource, enterDate } = buildOtherIncomeData(
        income,
        existingJobId,
        finalIndex,
      );
      updatedOtherSources.push(otherSource);
      Object.assign(mergedEnterDate, enterDate);
    });

    // Filter out deleted jobs from existing data
    const filteredCurrentJobs = currentJobs.filter((job) => {
      const jobIdNum =
        typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
      return !jobIdNum || !deletedJobIds.includes(jobIdNum);
    });

    const filteredPreviousJobs = previousJobs.filter((job) => {
      const jobIdNum =
        typeof job.job_id === "string" ? parseInt(job.job_id) : job.job_id;
      return !jobIdNum || !deletedJobIds.includes(jobIdNum);
    });

    const hasCurrentJobs = filteredCurrentJobs.length > 0;
    const hasOtherSources = updatedOtherSources.length > 0;
    const finalEmploymentStatus = hasCurrentJobs
      ? "1"
      : hasOtherSources
        ? "1"
        : employmentStatus === "employed"
          ? "1"
          : "0";

    const updateData: UpdateApplicationRequest = {
      ...incomeValidationHints("tab"),
      current_employment: filteredCurrentJobs,
      previous_employment: filteredPreviousJobs,
      other_source: updatedOtherSources,
      employment_status: finalEmploymentStatus,
      previous_employer: filteredPreviousJobs.length > 0 ? "1" : "0",
      ninja_reason: applicationData?.ninja_reason || "",
      underwriter_verified_income_other:
        updatedOtherSources.length > 0 ? "1" : "0",
      underwriter_verified_income_type:
        applicationData?.underwriter_verified_income_type || "net",
      enter_date:
        Object.keys(mergedEnterDate).length > 0 ? mergedEnterDate : {},
    };

    // Include deleted jobs if any
    if (deletedJobIds.length > 0) {
      updateData.deleted = {
        resident_job: deletedJobIds,
      };
    }

    return updateData;
  };

  // Auto-save-on-tab-change (Angular submitForm('fromtab') parity): persist the current
  // tab without navigating/dialogs, reusing the Save & Continue payload. Dirty-guarded.
  const autoSaveIncome = async (): Promise<void> => {
    if (!applicationData?.applicant_id) return;
    await dispatch(
      updateApplication({
        applicantId: applicationData.applicant_id,
        updateData: buildIncomeUpdateData(currentEmployers),
      }),
    ).unwrap();
    await dispatch(
      fetchApplicationById(applicationData.applicant_id),
    ).unwrap();
  };

  useTabAutoSave({
    name: "income",
    snapshot: buildIncomeUpdateData(currentEmployers),
    loadedKey: applicationData?.applicant_id,
    save: autoSaveIncome,
    isReadOnly,
  });

  // Handle Save & Continue - save all form data and check if bank is linked (exitAfterSave = true for Save & Exit)
  const handleSaveAndContinue = async (
    exitAfterSave = false,
    confirmNoEmployment = false,
  ) => {
    // If read-only mode, just navigate to next section without saving
    if (isReadOnly) {
      const historyPath = applicantId
        ? getTabPathWithApplicantId("history", applicantId)
        : "/applicant/application/history";
      if (applicantId) {
        navigate(historyPath, getTabNavigationOptions("history"));
      } else {
        navigate(historyPath);
      }
      return;
    }

    if (!applicationData?.applicant_id) {
      showWarning(
        "Application not found. Please return to the dashboard and try again.",
      );
      return;
    }

    // Validate all fields before saving
    const errorMessages: string[] = [];

    // For Save & Continue (not Exit): check bank linked when NINJA_REQUIRED === 'YES'.
    // Matches Angular's submitForm gate which only blocks when ninjaConfig.NINJA_REQUIRED === 'YES'
    // and there is no bank_name and no linked Ninja accounts.
    if (!exitAfterSave && bankLinking.ninjaConfig?.NINJA_REQUIRED === 'YES') {
      const hasNinjaAccounts =
        bankLinking.ninjaAccounts && bankLinking.ninjaAccounts.length > 0;
      const hasUploadedFiles =
        Array.isArray(applicationData?.ninja_income_proof) &&
        applicationData.ninja_income_proof.length > 0;
      const hasBankName =
        applicationData?.bank_name && applicationData.bank_name.trim() !== "";
      const hasUploadedBank = hasUploadedFiles || hasBankName;

      if (!hasNinjaAccounts && !hasUploadedBank) {
        setShowLinkBankModal(true);
        return;
      }
    }

    // Check for "No Employment Details" warning (Blank Page)
    // Only show when employment status IS selected but no details provided.
    // When employmentStatus === null (nothing selected), fall through to the validation
    // error below so the user gets "Please select your employment status" instead.
    if (
      !confirmNoEmployment &&
      employmentStatus !== null &&
      currentEmployers.length === 0 &&
      otherIncomes.length === 0
    ) {
      setShowNoEmploymentModal(true);
      return;
    }

    // Validate employment status
    if (!employmentStatus) {
      errorMessages.push(
        "Employment Status: Please select your employment status",
      );
    }

    // Recalculate distance only for employers missing distance or with changed address
    let employersForSave = currentEmployers;
    if (
      employmentStatus === "employed" &&
      currentEmployers.some((employer) => needsEmploymentDistanceRecall(employer))
    ) {
      employersForSave =
        await refreshAllCurrentEmployerDistancesBeforeSave(currentEmployers);
      setCurrentEmployers(employersForSave);
    }

    // Validate current employers if employed
    if (employmentStatus === "employed") {
      if (employersForSave.length === 0) {
        // Only require employer if we haven't confirmed "No Employment"
        // If user confirmed (Popup -> Continue), we allow empty employer list
        // If user has Other Income but no Employer, we still require Employer (didn't trigger blank page modal)
        if (!confirmNoEmployment) {
          errorMessages.push(
            "Current Employer: At least one current employer is required",
          );
        }
      } else {
        employersForSave.forEach((employer, index) => {
          const errors = validateCurrentEmployer(employer);
          if (Object.keys(errors).length > 0) {
            Object.entries(errors).forEach(([field, message]) => {
              const fieldLabel = getFieldLabel(field);
              errorMessages.push(
                `Current Employer ${index + 1} - ${fieldLabel}: ${message}`,
              );
            });
          }
        });
      }
    }

    // Validate previous employers
    previousEmployers.forEach((employer, index) => {
      const errors = validatePreviousEmployer(employer);
      if (Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([field, message]) => {
          const fieldLabel = getFieldLabel(field);
          errorMessages.push(
            `Previous Employer ${index + 1} - ${fieldLabel}: ${message}`,
          );
        });
      }
    });

    // Validate other incomes
    otherIncomes.forEach((income, index) => {
      const errors = validateOtherIncome(income);
      if (Object.keys(errors).length > 0) {
        Object.entries(errors).forEach(([field, message]) => {
          const fieldLabel = getFieldLabel(field);
          errorMessages.push(
            `Other Income ${index + 1} - ${fieldLabel}: ${message}`,
          );
        });
      }
    });

    // If there are validation errors, show popup and return
    if (errorMessages.length > 0) {
      setValidationModalMessage(errorMessages.join("\n"));
      setShowValidationModal(true);
      return;
    }

    if (exitAfterSave) {
      setIsSavingExit(true);
    } else {
      setIsSavingAndContinuing(true);
    }

    try {
      // Build the full-tab payload (same builder as auto-save).
      const updateData = buildIncomeUpdateData(employersForSave);

      // Save to API - updateApplication now automatically fetches latest data
      await dispatch(
        updateApplication({
          applicantId: applicationData.applicant_id,
          updateData: updateData,
        }),
      ).unwrap();

      // Clear deleted job IDs after successful save
      if (deletedJobIds.length > 0) {
        setDeletedJobIds([]);
      }

      // Data is saved: go to dashboard (Save & Exit) or next page (Save & Continue)
      if (exitAfterSave) {
        navigate("/applicant/dashboard", { replace: true });
      } else {
        const historyPath = applicantId
          ? getTabPathWithApplicantId("history", applicantId)
          : "/applicant/application/history";
        if (applicantId) {
          navigate(historyPath, {
            ...getTabNavigationOptions("history"),
            replace: true,
          });
        } else {
          navigate(historyPath, { replace: true });
        }
      }
    } catch (error) {
      sendAnalytics('autosave', 'resident application info error', applicationData?.applicant_id);
      // Don't navigate if save failed
      const errorMessage =
        typeof error === "string"
          ? error
          : (error as { message?: string })?.message ||
            "Unable to save income and employment. Please try again.";
      setValidationModalMessage(errorMessage);
      setShowValidationModal(true);
      setIsSavingAndContinuing(false);
      setIsSavingExit(false);
      return;
    } finally {
      setIsSavingAndContinuing(false);
      setIsSavingExit(false);
    }
  };

  // Handle file preview
  const handleFilePreview = async (file: {
    filepath?: string;
    name: string;
    attachment_id?: number;
  }) => {
    if (!file.filepath) {
      return;
    }

    const applicantId = applicationData?.applicant_id;
    if (!applicantId) {
      return;
    }

    // If we have attachment_id, use secure preview with token
    if (file.attachment_id) {
      await ensureFileToken();
      // Collect all files with attachment_id from ninja_income_proof to create filesList
      const ninjaIncomeProof = applicationData.ninja_income_proof || [];
      const allFilesWithIds = Array.isArray(ninjaIncomeProof)
        ? ninjaIncomeProof.filter(
            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
              f.attachment_id != null,
          )
        : [];

      // For image viewer navigation: include only image files (exclude PDFs)
      const imageFilesWithIds = allFilesWithIds.filter((f) =>
        isImageFile(f.filename || f.name || f.filepath || ""),
      );

      // Generate URLs for image files only to create filesList for navigation (uses fresh token)
      const filesList =
        imageFilesWithIds.length > 1
          ? imageFilesWithIds.map((f) =>
              generateFilePreviewUrl(
                f.attachment_id,
                "PROOF_OF_INCOME",
                applicantId,
                "APPLICATION",
              ),
            )
          : [];

      await openFilePreview(
        file.attachment_id,
        "PROOF_OF_INCOME",
        file.name || file.filepath,
        applicantId,
        "APPLICATION",
        filesList.length > 1 ? filesList : undefined, // Only pass if more than 1 image
      );
    } else {
      // Otherwise, use direct path preview (for stream-file URLs)
      // The filepath from API should be in format: income_proof/142533-5111382-libertyrent_icon+1+1.jpg
      // If it's already a full URL, extract just the path parameter
      let filePath = file.filepath.trim();

      // Check if filepath is already a full stream-file URL
      if (filePath.includes("/stream-file?path=")) {
        try {
          const url = new URL(filePath);
          const pathParam = url.searchParams.get("path");
          if (pathParam) {
            filePath = decodeURIComponent(pathParam);
          }
        } catch {
          // If URL parsing fails, try regex extraction
          const match = filePath.match(/path=([^&]+)/);
          if (match) {
            filePath = decodeURIComponent(match[1]);
          }
        }
      } else if (
        filePath.startsWith("http://") ||
        filePath.startsWith("https://")
      ) {
        // If it's a full URL but not stream-file, extract pathname
        try {
          const url = new URL(filePath);
          filePath = url.pathname.replace(/^\//, "");
        } catch {
          return;
        }
      }

      // Remove leading slash if present
      filePath = filePath.replace(/^\//, "");

      // Construct the preview URL - filePath should be like: income_proof/142533-5111382-libertyrent_icon+1+1.jpg
      const previewUrl = buildStreamFileUrl(filePath);

      // For stream-file URLs, collect all files from ninja_income_proof that don't have attachment_id
      const ninjaIncomeProof = applicationData.ninja_income_proof || [];
      const allFilesWithoutIds = Array.isArray(ninjaIncomeProof)
        ? ninjaIncomeProof.filter(
            (f): f is ApplicationFileAttachment & { filepath: string } =>
              !f.attachment_id && !!f.filepath,
          )
        : [];

      // For image viewer navigation: include only image files (exclude PDFs)
      const imageFilesWithoutIds = allFilesWithoutIds.filter((f) =>
        isImageFile(f.filename || f.name || f.filepath || ""),
      );

      // Generate stream-file URLs for image files only
      const filesList = imageFilesWithoutIds
        .map((f) => {
          let fPath = f.filepath.trim();
          if (fPath.includes("/stream-file?path=")) {
            try {
              const url = new URL(fPath);
              const pathParam = url.searchParams.get("path");
              if (pathParam) {
                fPath = decodeURIComponent(pathParam);
              }
            } catch {
              const match = fPath.match(/path=([^&]+)/);
              if (match) {
                fPath = decodeURIComponent(match[1]);
              }
            }
          } else if (
            fPath.startsWith("http://") ||
            fPath.startsWith("https://")
          ) {
            try {
              const url = new URL(fPath);
              fPath = url.pathname.replace(/^\//, "");
            } catch {
              // Skip invalid URLs
              return null;
            }
          }
          fPath = fPath.replace(/^\//, "");
          return buildStreamFileUrl(fPath);
        })
        .filter((url): url is string => url !== null);

      // Open in file-handlers component with filename
      openFilePreviewByPath(
        previewUrl,
        file.name,
        filesList.length > 1 ? filesList : undefined,
      );
    }
  };

  // Preview for proof_of_income files (general Proof of Income section)
  const handleProofOfIncomeFilePreview = async (file: {
    filepath?: string;
    name: string;
    attachment_id?: number;
  }) => {
    const applicantIdForPreview = applicationData?.applicant_id;
    if (!applicantIdForPreview) return;
    if (!file.filepath && !file.attachment_id) return;
    const proofList = applicationData?.proof_of_income || [];
    if (file.attachment_id) {
      await ensureFileToken();
      const allWithIds = Array.isArray(proofList)
        ? proofList.filter(
            (f): f is ApplicationFileAttachment & { attachment_id: number } =>
              f.attachment_id != null,
          )
        : [];
      // For image viewer navigation: include only image files (exclude PDFs)
      const imageFilesWithIds = allWithIds.filter((f) =>
        isImageFile(f.filename || f.name || f.filepath || ""),
      );
      const filesList =
        imageFilesWithIds.length > 1
          ? imageFilesWithIds.map((f) =>
              generateFilePreviewUrl(
                f.attachment_id,
                "PROOF_OF_INCOME",
                applicantIdForPreview,
                "APPLICATION",
              ),
            )
          : [];
      await openFilePreview(
        file.attachment_id,
        "PROOF_OF_INCOME",
        file.name || file.filepath || "document",
        applicantIdForPreview,
        "APPLICATION",
        filesList.length > 1 ? filesList : undefined,
      );
    } else if (file.filepath) {
      let filePath = file.filepath.trim();
      if (filePath.includes("/stream-file?path=")) {
        try {
          const url = new URL(filePath);
          const pathParam = url.searchParams.get("path");
          if (pathParam) filePath = decodeURIComponent(pathParam);
        } catch {
          const match = filePath.match(/path=([^&]+)/);
          if (match) filePath = decodeURIComponent(match[1]);
        }
      } else if (filePath.startsWith("http")) {
        try {
          filePath = new URL(filePath).pathname.replace(/^\//, "");
        } catch {
          return;
        }
      }
      filePath = filePath.replace(/^\//, "");
      const previewUrl = buildStreamFileUrl(filePath);
      const allWithoutIds = Array.isArray(proofList)
        ? proofList.filter(
            (f): f is ApplicationFileAttachment & { filepath: string } =>
              !f.attachment_id && !!f.filepath,
          )
        : [];
      // For image viewer navigation: include only image files (exclude PDFs)
      const imageFilesWithoutIds = allWithoutIds.filter((f) =>
        isImageFile(f.filename || f.name || f.filepath || ""),
      );
      const filesList = imageFilesWithoutIds.map((f) => {
        let fPath = (f.filepath || "").trim();
        if (fPath.includes("/stream-file?path=")) {
          try {
            const url = new URL(fPath);
            const p = url.searchParams.get("path");
            if (p) fPath = decodeURIComponent(p);
          } catch {
            const m = fPath.match(/path=([^&]+)/);
            if (m) fPath = decodeURIComponent(m[1]);
          }
        }
        fPath = fPath.replace(/^\//, "");
        return buildStreamFileUrl(fPath);
      });
      openFilePreviewByPath(
        previewUrl,
        file.name,
        filesList.length > 1 ? filesList : undefined,
      );
    }
  };

  const PROOF_OF_INCOME_MAX_FILES = 6;
  const proofOfIncomeMaxMb =
    applicationData?.applicant_status === "AWAITING" ||
      applicationData?.application_status === "AWAITING"
      ? 10
      : 5;

  const handleProofOfIncomeModalSubmit = async (files: File[]) => {
    if (!applicationData?.applicant_id) return;
    setProofOfIncomeMessage(null);
    const existingProofCount = Array.isArray(applicationData.proof_of_income)
      ? applicationData.proof_of_income.length
      : 0;
    const allowedNew = Math.max(
      0,
      PROOF_OF_INCOME_MAX_FILES - existingProofCount,
    );
    const filesToUpload = files.slice(0, allowedNew);
    if (filesToUpload.length === 0) {
      return;
    }
    const maxBytes = proofOfIncomeMaxMb * 1024 * 1024;
    const oversized = filesToUpload.find((f) => f.size > maxBytes);
    if (oversized) {
      setProofOfIncomeMessage({
        type: "error",
        text: `File Size Exceeds Allowable Limit of ${proofOfIncomeMaxMb} MB.`,
      });
      throw new Error("File too large");
    }
    setProofOfIncomeUploading(true);
    try {
      await applicationService.uploadProofOfIncomeFiles(
        applicationData.applicant_id,
        filesToUpload,
      );
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();
      setProofOfIncomeMessage({
        type: "success",
        text:
          filesToUpload.length > 1
            ? "Proof of income files uploaded successfully."
            : "Proof of income file uploaded successfully.",
      });
      setTimeout(() => setProofOfIncomeMessage(null), 5000);
    } catch (err) {
      setProofOfIncomeMessage({
        type: "error",
        text:
          (err as { message?: string })?.message || "Failed to upload file.",
      });
      throw err;
    } finally {
      setProofOfIncomeUploading(false);
    }
  };

  const handleRemoveProofOfIncomeFile = async (
    attachmentId: number | string,
  ) => {
    if (!applicationData?.applicant_id || isReadOnly) return;
    setProofOfIncomeMessage(null);
    try {
      await applicationService.deleteProofOfIncomeFiles(
        applicationData.applicant_id,
        [attachmentId],
      );
      await dispatch(
        fetchApplicationById(applicationData.applicant_id),
      ).unwrap();
      setProofOfIncomeMessage({ type: "success", text: "File removed." });
      setTimeout(() => setProofOfIncomeMessage(null), 3000);
    } catch (err) {
      setProofOfIncomeMessage({
        type: "error",
        text:
          (err as { message?: string })?.message || "Failed to remove file.",
      });
    }
  };

  const handleRemoveNinjaIncomeProofFile = async (
    attachmentId: number | string,
  ) => {
    if (!applicationData?.applicant_id || isReadOnly) return;
    setDeletingNinjaFileId(attachmentId);
    setBankMessage(null);
    try {
      await applicationService.updateResidentApplication(
        applicationData.applicant_id,
        {
          ...incomeValidationHints("section"),
          deleted: { ninja_income_proof: [attachmentId] },
        } as UpdateApplicationRequest,
      );
      await dispatch(fetchApplicationById(applicationData.applicant_id)).unwrap();
      setBankMessage({ type: "success", text: "File removed." });
      setTimeout(() => setBankMessage(null), 3000);
    } catch (err) {
      const errorMessage =
        typeof err === "string"
          ? err
          : (err as { message?: string })?.message ||
            "Failed to remove file. Please try again.";
      setValidationModalMessage(errorMessage);
      setShowValidationModal(true);
    } finally {
      setDeletingNinjaFileId(null);
    }
  };

  const hasLinkedBankConnection =
    (bankLinking.ninjaAccounts?.length ?? 0) > 0 ||
    (bankLinking.plaidAccounts?.length ?? 0) > 0;

  const isAwaitingStatus =
    applicationData?.applicant_status === "AWAITING" ||
    applicationData?.application_status === "AWAITING";

  const hasNinjaIncomeProofDocs =
    Array.isArray(applicationData?.ninja_income_proof) &&
    applicationData.ninja_income_proof.length > 0;

  const hasProofOfIncomeDocs =
    Array.isArray(applicationData?.proof_of_income) &&
    applicationData.proof_of_income.length > 0;

  const hasManualBankNameForDocs =
    !!applicationData?.bank_name && applicationData.bank_name.trim() !== "";

  /** NINJA_INCOME + INCOME uploads: awaiting, manual document path, or existing files to display */
  const showSupportingUploadsUnifiedSection =
    isAwaitingStatus ||
    hasNinjaIncomeProofDocs ||
    hasProofOfIncomeDocs ||
    (hasManualBankNameForDocs && !hasLinkedBankConnection)

  const showNinjaDocSubsection =
    !hasLinkedBankConnection &&
    (hasManualBankNameForDocs ||
      hasNinjaIncomeProofDocs ||
      choseDocumentUploads);

  const ninjaIncomeExistingCount = Array.isArray(
    applicationData?.ninja_income_proof,
  )
    ? applicationData!.ninja_income_proof!.length
    : 0;

  const ninjaProofMaxSizeMb =
    applicationData?.applicant_status === "AWAITING" ||
    applicationData?.application_status === "AWAITING"
      ? 10
      : 5;

  const showNinjaInlineUploadDropzone =
    showNinjaDocSubsection &&
    !isAwaitingStatus &&
    (!isReadOnly || applicationData?.applicant_status === "INCOMPLETE") &&
    ninjaIncomeExistingCount < 6;

  const ninjaExistingFileNames = Array.isArray(
    applicationData?.ninja_income_proof,
  )
    ? (
        applicationData!.ninja_income_proof! as {
          filename?: string;
          name?: string;
        }[]
      ).map((f) => f.filename || f.name || "")
    : [];

  return (
    <div>
      {/* Header - Resident Application */}
      <ApplicationHeader
        {...getApplicationHeaderProps(
          applicationData,
          APPLICANT_CONTENT.application.status.creditReportPending,
        )}
        balanceDue={
          applicationData?.balance_due
            ? Number(applicationData.balance_due)
            : undefined
        }
        paymentDueDate={applicationData?.payment_due_date}
        applicantStatus={
          applicationData?.applicant_status ||
          applicationData?.application_status ||
          ""
        }
      />

      {/* Tab Navigation */}
      <ApplicantTabNavigation />

      {/* Content - form-readonly when applicant_lock so header/stepper stay clickable (per Angular) */}
      <div className={applicationData?.applicant_lock === "1" ? "form-readonly" : undefined}>
      <div data-testid="income-page" className="p-3 sm:p-6 bg-gray-50 min-h-screen">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900">
                {APPLICANT_CONTENT.application.steps.income.name}
              </h2>
            </div>
          </div>

          {/* Warning Message */}
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 rounded-lg p-4 mb-6 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-800 font-medium">
              WARNING: If you do not provide at least two years of work history, Liberty Rent will request additional information, which could delay the application process
              </p>
            </div>
          </div>
          {/* Current Employer Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              <span className="font-bold">Current</span> Employer (list all
              jobs where you are currently employed or formal offers that you
              plan to accept)
            </h3>

            {/* Employment Status - MOVED INSIDE HERE */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Employment Status*
              </label>
              <div className="flex flex-wrap gap-4 mb-3 items-center">
                <button
                  type="button"
                  onClick={() => {
                    if (!isReadOnly) {
                      setEmploymentStatus("employed");
                      if (currentEmployers.length === 0) {
                        addCurrentEmployer();
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className={`px-6 py-2 border rounded-md transition-colors ${
                    employmentStatus === "employed"
                      ? "bg-[#1597ff] text-white border-[#1597ff]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Employed
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!isReadOnly) {
                      setEmploymentStatus("not-employed");
                      // Angular parity: "Not Employed" clears current employers (tracking saved
                      // jobs for deletion). The empty list drives the job-gap effect to recompute
                      // previous gaps against "no current job" and saves employment_status "0".
                      if (currentEmployers.length > 0) {
                        const removedSavedJobIds = currentEmployers
                          .filter((emp) => emp.jobId)
                          .map((emp) => emp.jobId!);
                        if (removedSavedJobIds.length > 0) {
                          setDeletedJobIds((prev) => [
                            ...prev,
                            ...removedSavedJobIds,
                          ]);
                        }
                        setCurrentEmployers([]);
                      }
                    }
                  }}
                  disabled={isReadOnly}
                  className={`px-6 py-2 border rounded-md transition-colors ${
                    employmentStatus === "not-employed"
                      ? "bg-[#1597ff] text-white border-[#1597ff]"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Not Employed
                </button>
                {!employmentStatus && (
                  <span className="text-sm text-red-600">*Select value</span>
                )}
              </div>
              {(!employmentStatus || employmentStatus === "not-employed") && (
                <p className="text-sm text-gray-600">
                  (Note: if 'Not Employed' is selected please provide Other
                  Sources of Income below)
                </p>
              )}
            </div>

            {/* Show employer cards ONLY when employed */}
            {employmentStatus === "employed" && (
              <>
                {currentEmployers.map((employer) => (
                  <div
                    key={employer.id}
                    className="mb-6 p-6 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="mb-4">
                      <div className="flex items-center mb-3 relative">
                        <label className="block text-sm font-medium text-gray-700">
                          Type of Employment*
                        </label>
                        {!isReadOnly && (
                          <button
                            type="button"
                            onClick={() => removeCurrentEmployer(employer.id)}
                            className="text-red-600 hover:text-red-700 text-sm font-medium ml-auto flex-shrink-0"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <div className="flex gap-4">
                        <label
                          className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <input
                            type="radio"
                            name={`current-employment-type-${employer.id}`}
                            checked={employer.employmentType === "full-time"}
                            onChange={() => {
                              if (!isReadOnly) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "employmentType",
                                  "full-time",
                                );
                                if (currentEmployerErrors[employer.id]?.employmentType) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.employmentType;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                            }}
                            disabled={isReadOnly}
                            className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                          />
                          <span className="text-sm text-gray-700">
                            Full Time (More Than 32 Hrs/ Week)
                          </span>
                        </label>
                        <label
                          className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <input
                            type="radio"
                            name={`current-employment-type-${employer.id}`}
                            checked={employer.employmentType === "part-time"}
                            onChange={() => {
                              if (!isReadOnly) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "employmentType",
                                  "part-time",
                                );
                                if (currentEmployerErrors[employer.id]?.employmentType) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.employmentType;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                            }}
                            disabled={isReadOnly}
                            className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                          />
                          <span className="text-sm text-gray-700">Part Time</span>
                        </label>
                        <label
                          className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <input
                            type="radio"
                            name={`current-employment-type-${employer.id}`}
                            checked={employer.employmentType === "self-employed"}
                            onChange={() => {
                              if (!isReadOnly) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "employmentType",
                                  "self-employed",
                                );
                                if (currentEmployerErrors[employer.id]?.employmentType) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.employmentType;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                            }}
                            disabled={isReadOnly}
                            className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                          />
                          <span className="text-sm text-gray-700">Self-Employed</span>
                        </label>
                      </div>
                      {validateForm &&
                        currentEmployerErrors[employer.id]?.employmentType && (
                          <div className="mt-1 text-sm text-red-600">
                            {currentEmployerErrors[employer.id].employmentType}
                          </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Company Name*
                        </label>
                        <input
                          type="text"
                          value={employer.companyName}
                          onChange={(e) => {
                            updateCurrentEmployer(
                              employer.id,
                              "companyName",
                              e.target.value,
                            );
                          }}
                          placeholder="Company Name"
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border rounded-md bg-white ${
                            validateForm &&
                            currentEmployerErrors[employer.id]?.companyName
                              ? "border-red-500"
                              : "border-gray-300"
                          } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                        />
                        {validateForm &&
                          currentEmployerErrors[employer.id]?.companyName && (
                            <div className="mt-1 text-sm text-red-600">
                              {currentEmployerErrors[employer.id].companyName}
                            </div>
                          )}
                      </div>
                      <div className="md:col-span-2">
                        <div>
                          <EmployerAddressDetail
                            value={employer.address}
                            propertyAddress={getPropertyAddress()}
                            states={states}
                            isLoadingStates={isLoadingStates}
                            onChange={(value) =>
                              updateCurrentEmployer(employer.id, "address", value)
                            }
                            onPlaceSelect={(place) =>
                              handleEmployerAddressSelect(employer.id, place)
                            }
                            onAddressFieldBlur={(fields) =>
                              validateDistance(employer.id, fields)
                            }
                            disabled={isReadOnly}
                            initialFields={{
                              streetName: employer.streetName || "",
                              aptUnit: employer.aptUnit || "",
                              city: employer.city || "",
                              state: employer.state || "",
                              zipCode: employer.zipCode || "",
                              country: employer.country || "",
                              otherOutsideUS: employer.otherOutsideUS || false,
                            }}
                            onFieldsChange={(fields) => {
                              if (fields.otherOutsideUS !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "otherOutsideUS",
                                  fields.otherOutsideUS,
                                );
                              }
                              if (fields.streetName !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "streetName",
                                  fields.streetName,
                                );
                                if (
                                  fields.streetName.trim() &&
                                  currentEmployerErrors[employer.id]?.streetName
                                ) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.streetName;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                              if (fields.aptUnit !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "aptUnit",
                                  fields.aptUnit,
                                );
                              }
                              if (fields.city !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "city",
                                  fields.city,
                                );
                                if (
                                  fields.city.trim() &&
                                  currentEmployerErrors[employer.id]?.city
                                ) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.city;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                              if (fields.state !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "state",
                                  fields.state,
                                );
                                if (
                                  fields.state.trim() &&
                                  currentEmployerErrors[employer.id]?.state
                                ) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.state;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                              if (fields.zipCode !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "zipCode",
                                  fields.zipCode,
                                );
                                if (
                                  fields.zipCode.trim() &&
                                  currentEmployerErrors[employer.id]?.zipCode
                                ) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.zipCode;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                              if (fields.country !== undefined) {
                                updateCurrentEmployer(
                                  employer.id,
                                  "country",
                                  fields.country,
                                );
                                if (
                                  fields.country.trim() &&
                                  currentEmployerErrors[employer.id]?.country
                                ) {
                                  setCurrentEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.country;
                                    return { ...prev, [employer.id]: newErrors };
                                  });
                                }
                              }
                            }}
                            placeholder="Enter employer address"
                            label="Address *"
                            fieldErrors={{
                              streetName: validateForm
                                ? currentEmployerErrors[employer.id]?.streetName
                                : undefined,
                              city: validateForm
                                ? currentEmployerErrors[employer.id]?.city
                                : undefined,
                              state: validateForm
                                ? currentEmployerErrors[employer.id]?.state
                                : undefined,
                              zipCode: validateForm
                                ? currentEmployerErrors[employer.id]?.zipCode
                                : undefined,
                              country: validateForm
                                ? currentEmployerErrors[employer.id]?.country
                                : undefined,
                            }}
                            showValidation={validateForm}
                          />
                          {calculatingDistance === employer.id && (
                            <p className="text-xs text-blue-600">
                              Calculating distance...
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Monthly Income*
                        </label>
                        <input
                          type="text"
                          value={
                            employer.monthlyIncome
                              ? formatCurrencyInput(employer.monthlyIncome)
                              : ""
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            const numericValue = getNumericValue(inputValue);
                            const yearly = calculateYearlyIncome(numericValue, "monthly");
                            setCurrentEmployers((prev) =>
                              prev.map((emp) =>
                                emp.id === employer.id
                                  ? {
                                      ...emp,
                                      monthlyIncome: numericValue,
                                      yearlyCalculatedIncome: yearly,
                                    }
                                  : emp,
                              ),
                            );
                            if (currentEmployerErrors[employer.id]?.monthlyIncome) {
                              setCurrentEmployerErrors((prev) => {
                                const newErrors = { ...prev[employer.id] };
                                delete newErrors.monthlyIncome;
                                return { ...prev, [employer.id]: newErrors };
                              });
                            }
                          }}
                          onBlur={() => {
                            if (employer.monthlyIncome) {
                              const numericValue = getNumericValue(
                                formatCurrencyInput(employer.monthlyIncome, true),
                              );
                              setCurrentEmployers((prev) =>
                                prev.map((emp) =>
                                  emp.id === employer.id
                                    ? { ...emp, monthlyIncome: numericValue }
                                    : emp,
                                ),
                              );
                            }
                          }}
                          placeholder="$0.00"
                          disabled={isReadOnly}
                          readOnly={isReadOnly}
                          maxLength={10}
                          autoComplete="off"
                          className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            validateForm &&
                            currentEmployerErrors[employer.id]?.monthlyIncome
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                          }`}
                        />
                        {validateForm &&
                          currentEmployerErrors[employer.id]?.monthlyIncome && (
                            <div className="mt-1 text-sm text-red-600">
                              {currentEmployerErrors[employer.id].monthlyIncome}
                            </div>
                          )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Yearly Calculated Income
                        </label>
                        <input
                          type="text"
                          value={employer.yearlyCalculatedIncome}
                          readOnly={true}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Job Start Date*
                        </label>
                        <DatePicker
                          value={convertToDateInputFormat(employer.jobStartDate)}
                          onChange={(value) => {
                            const displayValue = convertToDisplayFormat(value);
                            const startDate = new Date(
                              convertToAPIDateFormat(displayValue),
                            );
                            startDate.setHours(0, 0, 0, 0);
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const months =
                              startDate <= today
                                ? employmentMonthsAtJob(displayValue)
                                : 0;
                            setCurrentEmployers((prev) =>
                              prev.map((emp) =>
                                emp.id === employer.id
                                  ? {
                                      ...emp,
                                      jobStartDate: displayValue,
                                      monthsAtJob: months.toString(),
                                    }
                                  : emp,
                              ),
                            );
                            // Previous-employer gaps are recomputed centrally by the job-gap effect.
                            if (currentEmployerErrors[employer.id]?.jobStartDate) {
                              setCurrentEmployerErrors((prev) => {
                                const newErrors = { ...prev[employer.id] };
                                delete newErrors.jobStartDate;
                                return { ...prev, [employer.id]: newErrors };
                              });
                            }
                            if (
                              currentEmployerErrors[employer.id]?.monthsAtJob &&
                              (months >= 0 || isFutureEmploymentStartDate(displayValue))
                            ) {
                              setCurrentEmployerErrors((prev) => {
                                const newErrors = { ...prev[employer.id] };
                                delete newErrors.monthsAtJob;
                                return { ...prev, [employer.id]: newErrors };
                              });
                            }
                          }}
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                          allowFutureDates={true}
                          maxDate={getMaxCurrentEmploymentStartDate()}
                          showHelperText={false}
                          disabled={isReadOnly}
                          error={
                            validateForm
                              ? currentEmployerErrors[employer.id]?.jobStartDate
                              : undefined
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Number of months at this job*{" "}
                          <span className="text-xs text-gray-500 font-normal">
                            (Auto-calculated)
                          </span>
                        </label>
                        <input
                          type="text"
                          value={employer.monthsAtJob}
                          onChange={(e) =>
                            updateCurrentEmployer(
                              employer.id,
                              "monthsAtJob",
                              e.target.value,
                            )
                          }
                          placeholder="Auto-calculated from start date"
                          className={`w-full px-3 py-2 border rounded-md bg-gray-50 ${
                            validateForm &&
                            currentEmployerErrors[employer.id]?.monthsAtJob
                              ? "border-red-500"
                              : "border-gray-300"
                          }`}
                          readOnly={true}
                          disabled={true}
                        />
                        {validateForm &&
                          currentEmployerErrors[employer.id]?.monthsAtJob && (
                            <div className="mt-1 text-sm text-red-600">
                              {currentEmployerErrors[employer.id].monthsAtJob}
                            </div>
                          )}
                      </div>
                      <div className="max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          HR/Manager Contact Email
                        </label>
                        <input
                          type="email"
                          value={employer.hrManagerEmail}
                          onChange={(e) => {
                            updateCurrentEmployer(
                              employer.id,
                              "hrManagerEmail",
                              e.target.value,
                            );
                            if (currentEmployerErrors[employer.id]?.hrManagerEmail) {
                              setCurrentEmployerErrors((prev) => {
                                const newErrors = { ...prev[employer.id] };
                                delete newErrors.hrManagerEmail;
                                return { ...prev, [employer.id]: newErrors };
                              });
                            }
                          }}
                          placeholder="Please enter a company-issued email address."
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all ${
                            validateForm &&
                            currentEmployerErrors[employer.id]?.hrManagerEmail
                              ? "border-red-500 focus:ring-red-500"
                              : "border-gray-300"
                          } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                        />
                        {validateForm &&
                          currentEmployerErrors[employer.id]?.hrManagerEmail && (
                            <div className="mt-1 text-sm text-red-600">
                              {currentEmployerErrors[employer.id].hrManagerEmail}
                            </div>
                          )}
                      </div>
                      <div></div>
                    </div>

                    {/* Website field - only show for Self-Employed */}
                    {employer.employmentType === "self-employed" && (
                      <div className="mb-4 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          value={employer.website || ""}
                          onChange={(e) =>
                            updateCurrentEmployer(employer.id, "website", e.target.value)
                          }
                          placeholder="https://example.com"
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""
                          }`}
                        />
                      </div>
                    )}

                    {/* Distance warning */}
                    {currentEmployerRequiresDistanceWarning(employer) && (
                        <div className="mb-4 max-w-full">
                          <div
                            className="rounded px-4 py-3 mb-2 text-sm"
                            style={{
                              background: "#fef9ec",
                              border: "1px solid #eabf14",
                              color: "#94845b",
                            }}
                            role="alert"
                          >
                            <p className="mb-0">
                              {APPLICANT_CONTENT.errors.employment.distanceWarning}
                            </p>
                          </div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Describe your employment/commuting situation, e.g.,
                            remote worker, satellite office, etc.{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            value={employer.distanceExplanation || ""}
                            onChange={(e) => {
                              if (isReadOnly) return;
                              updateCurrentEmployer(
                                employer.id,
                                "distanceExplanation",
                                e.target.value,
                              );
                              if (
                                currentEmployerErrors[employer.id]?.distanceExplanation
                              ) {
                                setCurrentEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.distanceExplanation;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }}
                            disabled={isReadOnly}
                            placeholder="Please Explain"
                            rows={2}
                            maxLength={200}
                            className={`w-full px-3 py-2 border rounded-md bg-white text-sm ${
                              validateForm &&
                              currentEmployerErrors[employer.id]?.distanceExplanation
                                ? "border-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {validateForm &&
                            currentEmployerErrors[employer.id]?.distanceExplanation && (
                              <div className="mt-1 text-sm text-red-600">
                                {currentEmployerErrors[employer.id].distanceExplanation}
                              </div>
                            )}
                        </div>
                      )}

                    {/* Save success message */}
                    {saveMessage[employer.id]?.text &&
                      saveMessage[employer.id].type === "success" && (
                        <div className="mb-4 p-4 bg-green-100 border-2 border-green-400 rounded-lg shadow-md">
                          <div className="flex items-center">
                            <svg
                              className="w-5 h-5 text-green-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <p className="text-sm font-semibold text-green-800">
                              {saveMessage[employer.id].text}
                            </p>
                          </div>
                        </div>
                      )}

                    {/* Save error message */}
                    {saveMessage[employer.id]?.text &&
                      saveMessage[employer.id].type === "error" && (
                        <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 rounded-lg shadow-md">
                          <div className="flex items-center">
                            <svg
                              className="w-5 h-5 text-red-600 mr-2"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <p className="text-sm font-semibold text-red-800">
                              {saveMessage[employer.id].text}
                            </p>
                          </div>
                        </div>
                      )}

                    {!isReadOnly && (
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSaveCurrentEmployer(employer);
                          }}
                          disabled={
                            isSaving[employer.id] ||
                            calculatingDistance === employer.id
                          }
                          className="px-6 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isSaving[employer.id] ||
                          calculatingDistance === employer.id
                            ? "Saving..."
                            : "Save"}
                        </button>
                        {!employer.jobId && (
                          <button
                            type="button"
                            onClick={() => removeCurrentEmployer(employer.id)}
                            className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addCurrentEmployer}
                  disabled={isReadOnly}
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  Add Other <span className="font-bold">Current</span> Employers
                  (i.e. multiple part-time jobs)
                </button>
              </>
            )}
          </div>

          {/* Previous Employer Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Previous Employer
            </h3>

          <div className="mb-4">
            <div className="flex flex-wrap gap-4 mb-3 items-center">
              <button
                type="button"
                onClick={() => {
                  if (!isReadOnly) {
                    setHasPreviousEmployer(true);
                    if (previousEmployers.length === 0) {
                      addPreviousEmployer();
                    }
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${
                  hasPreviousEmployer === true
                    ? "bg-[#1597ff] text-white border-[#1597ff]"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                I Have A Previous Employer
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (isReadOnly) return;
                  setHasPreviousEmployer(false);
                  setPreviousEmployers([]);
                  if (applicationData?.applicant_id) {
                    try {
                      const updateData: UpdateApplicationRequest = {
                        ...incomeValidationHints("section"),
                        previous_employer: "0",
                        previous_employment: [],
                        enter_date: {},
                      };
                      await dispatch(
                        updateApplication({
                          applicantId: applicationData.applicant_id,
                          updateData: updateData,
                        }),
                      ).unwrap();
                    } catch (error) {
                      const errorMessage =
                        typeof error === "string"
                          ? error
                          : (error as { message?: string })?.message ||
                            "Unable to update previous employer selection. Please try again.";
                      setValidationModalMessage(errorMessage);
                      setShowValidationModal(true);
                    }
                  }
                }}
                disabled={isReadOnly}
                className={`px-6 py-2 border rounded-md transition-colors ${
                  hasPreviousEmployer === false
                    ? "bg-[#1597ff] text-white border-[#1597ff]"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                } ${isReadOnly ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                I Have No Previous Employer
              </button>
              {hasPreviousEmployer === null && (
                <span className="text-sm text-red-600">*Select value</span>
              )}
            </div>
          </div>

            {hasPreviousEmployer === true &&
              previousEmployers.map((employer, index) => (
                <div
                  key={employer.id}
                  data-testid={`income-previous-employer-${index}`}
                  className="mb-6 p-6 bg-white border border-gray-200 rounded-lg"
                >
                  <div className="mb-4">
                    <div className="flex items-center mb-3 relative">
                      <label className="block text-sm font-medium text-gray-700">
                        Type of Employment*
                      </label>
                      {!isReadOnly && (
                        <button
                          type="button"
                          data-testid={`income-previous-employer-${index}-remove-button`}
                          onClick={() => removePreviousEmployer(employer.id)}
                          className="text-red-600 hover:text-red-700 text-sm font-medium ml-auto flex-shrink-0"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div className="flex gap-4">
                      <label
                        className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name={`previous-employment-type-${employer.id}`}
                          checked={employer.employmentType === "full-time"}
                          onChange={() => {
                            if (!isReadOnly) {
                              updatePreviousEmployer(
                                employer.id,
                                "employmentType",
                                "full-time",
                              );
                              if (
                                previousEmployerErrors[employer.id]
                                  ?.employmentType
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.employmentType;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                          }}
                          disabled={isReadOnly}
                          className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                        />
                        <span className="text-sm text-gray-700">
                          Full Time (More Than 32 Hrs/ Week)
                        </span>
                      </label>
                      <label
                        className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name={`previous-employment-type-${employer.id}`}
                          checked={employer.employmentType === "part-time"}
                          onChange={() => {
                            if (!isReadOnly) {
                              updatePreviousEmployer(
                                employer.id,
                                "employmentType",
                                "part-time",
                              );
                              if (
                                previousEmployerErrors[employer.id]
                                  ?.employmentType
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.employmentType;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                          }}
                          disabled={isReadOnly}
                          className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                        />
                        <span className="text-sm text-gray-700">Part Time</span>
                      </label>
                      <label
                        className={`flex items-center ${isReadOnly ? "cursor-not-allowed" : "cursor-pointer"}`}
                      >
                        <input
                          type="radio"
                          name={`previous-employment-type-${employer.id}`}
                          checked={employer.employmentType === "self-employed"}
                          onChange={() => {
                            if (!isReadOnly) {
                              updatePreviousEmployer(
                                employer.id,
                                "employmentType",
                                "self-employed",
                              );
                              if (
                                previousEmployerErrors[employer.id]
                                  ?.employmentType
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.employmentType;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                          }}
                          disabled={isReadOnly}
                          className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                        />
                        <span className="text-sm text-gray-700">
                          Self-Employed
                        </span>
                      </label>
                    </div>
                    {validateForm &&
                      previousEmployerErrors[employer.id]?.employmentType && (
                        <div className="mt-1 text-sm text-red-600">
                          {previousEmployerErrors[employer.id].employmentType}
                        </div>
                      )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name*
                      </label>
                      <input
                        type="text"
                        value={employer.companyName}
                        onChange={(e) => {
                          updatePreviousEmployer(
                            employer.id,
                            "companyName",
                            e.target.value,
                          );
                          if (
                            previousEmployerErrors[employer.id]?.companyName
                          ) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.companyName;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                        }}
                        placeholder="Company Name"
                        disabled={isReadOnly}
                        className={`w-full px-3 py-2 border rounded-md bg-white ${validateForm &&
                          previousEmployerErrors[employer.id]?.companyName
                          ? "border-red-500"
                          : "border-gray-300"
                          } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                      />
                      {validateForm &&
                        previousEmployerErrors[employer.id]?.companyName && (
                          <div className="mt-1 text-sm text-red-600">
                            {previousEmployerErrors[employer.id].companyName}
                          </div>
                        )}
                    </div>
                    <div className="md:col-span-2">
                      <div className="space-y-2">
                        <EmployerAddressDetail
                          value={employer.address}
                          states={states}
                          isLoadingStates={isLoadingStates}
                          onChange={(value) =>
                            updatePreviousEmployer(
                              employer.id,
                              "address",
                              value,
                            )
                          }
                          onPlaceSelect={(place) => {
                            const fullAddress = place.formatted_address || "";
                            const parsed = parseAddressComponents(place);
                            const streetLine = [parsed.streetNumber, parsed.streetName].filter(Boolean).join(" ").trim() || (parsed.streetName || "");
                            updatePreviousEmployer(
                              employer.id,
                              "address",
                              fullAddress,
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "streetName",
                              streetLine,
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "aptUnit",
                              parsed.aptUnit || "",
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "city",
                              parsed.city || "",
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "state",
                              parsed.state || "",
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "zipCode",
                              parsed.zipCode || "",
                            );
                            updatePreviousEmployer(
                              employer.id,
                              "country",
                              parsed.country || "",
                            );
                            const isOutsideUS =
                              parsed.country &&
                              parsed.country.toUpperCase() !== "US";
                            updatePreviousEmployer(
                              employer.id,
                              "otherOutsideUS",
                              isOutsideUS,
                            );
                          }}
                          disabled={isReadOnly}
                          initialFields={{
                            streetName: employer.streetName || "",
                            aptUnit: employer.aptUnit || "",
                            city: employer.city || "",
                            state: employer.state || "",
                            zipCode: employer.zipCode || "",
                            country: employer.country || "",
                            otherOutsideUS: employer.otherOutsideUS || false,
                          }}
                          onFieldsChange={(fields) => {
                            if (fields.otherOutsideUS !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "otherOutsideUS",
                                fields.otherOutsideUS,
                              );
                            }
                            if (fields.streetName !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "streetName",
                                fields.streetName,
                              );
                              // Clear error when user fills the field
                              if (
                                fields.streetName.trim() &&
                                previousEmployerErrors[employer.id]?.streetName
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.streetName;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                            if (fields.aptUnit !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "aptUnit",
                                fields.aptUnit,
                              );
                            }
                            if (fields.city !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "city",
                                fields.city,
                              );
                              // Clear error when user fills the field
                              if (
                                fields.city.trim() &&
                                previousEmployerErrors[employer.id]?.city
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.city;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                            if (fields.state !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "state",
                                fields.state,
                              );
                              // Clear error when user fills the field
                              if (
                                fields.state.trim() &&
                                previousEmployerErrors[employer.id]?.state
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.state;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                            if (fields.zipCode !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "zipCode",
                                fields.zipCode,
                              );
                              // Clear error when user fills the field
                              if (
                                fields.zipCode.trim() &&
                                previousEmployerErrors[employer.id]?.zipCode
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.zipCode;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                            if (fields.country !== undefined) {
                              updatePreviousEmployer(
                                employer.id,
                                "country",
                                fields.country,
                              );
                              // Clear error when user fills the field
                              if (
                                fields.country.trim() &&
                                previousEmployerErrors[employer.id]?.country
                              ) {
                                setPreviousEmployerErrors((prev) => {
                                  const newErrors = { ...prev[employer.id] };
                                  delete newErrors.country;
                                  return { ...prev, [employer.id]: newErrors };
                                });
                              }
                            }
                          }}
                          placeholder="Enter employer address"
                          label="Address *"
                          fieldErrors={{
                            streetName: validateForm
                              ? previousEmployerErrors[employer.id]?.streetName
                              : undefined,
                            city: validateForm
                              ? previousEmployerErrors[employer.id]?.city
                              : undefined,
                            state: validateForm
                              ? previousEmployerErrors[employer.id]?.state
                              : undefined,
                            zipCode: validateForm
                              ? previousEmployerErrors[employer.id]?.zipCode
                              : undefined,
                            country: validateForm
                              ? previousEmployerErrors[employer.id]?.country
                              : undefined,
                          }}
                          showValidation={validateForm}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Income*
                      </label>
                      <input
                        type="text"
                        value={
                          employer.monthlyIncome
                            ? formatCurrencyInput(employer.monthlyIncome)
                            : ""
                        }
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          // Allow user to type, store the raw numeric value
                          const numericValue = getNumericValue(inputValue);
                          const yearly = calculateYearlyIncome(
                            numericValue,
                            "monthly",
                          );
                          setPreviousEmployers((prev) =>
                            prev.map((emp) =>
                              emp.id === employer.id
                                ? {
                                  ...emp,
                                  monthlyIncome: numericValue,
                                  yearlyCalculatedIncome: yearly,
                                }
                                : emp,
                            ),
                          );
                          if (
                            previousEmployerErrors[employer.id]?.monthlyIncome
                          ) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.monthlyIncome;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                        }}
                        onBlur={() => {
                          if (employer.monthlyIncome) {
                            const numericValue = getNumericValue(formatCurrencyInput(employer.monthlyIncome, true));
                            setPreviousEmployers((prev) =>
                              prev.map((emp) =>
                                emp.id === employer.id
                                  ? { ...emp, monthlyIncome: numericValue }
                                  : emp,
                              ),
                            );
                          }
                        }}
                        placeholder="$0.00"
                        disabled={isReadOnly}
                        readOnly={isReadOnly}
                        maxLength={10}
                        autoComplete="off"
                        className={`w-full px-3 py-2 border rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${validateForm &&
                          previousEmployerErrors[employer.id]?.monthlyIncome
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300"
                          }`}
                      />
                      {validateForm &&
                        previousEmployerErrors[employer.id]?.monthlyIncome && (
                          <div className="mt-1 text-sm text-red-600">
                            {previousEmployerErrors[employer.id].monthlyIncome}
                          </div>
                        )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Yearly Calculated Income
                      </label>
                      <input
                        type="text"
                        value={employer.yearlyCalculatedIncome}
                        readOnly={true}
                        disabled={true}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job Start Date*
                      </label>
                      <DatePicker
                        value={convertToDateInputFormat(employer.jobStartDate)}
                        onChange={(value) => {
                          const displayValue = convertToDisplayFormat(value);

                          if (employer.jobEndDate && displayValue) {
                            const start = new Date(convertToAPIDateFormat(displayValue));
                            const end = new Date(convertToAPIDateFormat(employer.jobEndDate));
                            start.setHours(0, 0, 0, 0);
                            end.setHours(0, 0, 0, 0);
                            if (start > end) {
                              setPreviousEmployers((prev) =>
                                prev.map((emp) =>
                                  emp.id === employer.id
                                    ? { ...emp, jobStartDate: displayValue, jobEndDate: "", monthsAtJob: "0" }
                                    : emp,
                                ),
                              );
                              setValidationModalMessage("Job End Date should be after Job Start Date");
                              setShowValidationModal(true);
                              return;
                            }
                          }

                          // Angular: job_duration = moment(end).diff(start, 'months') + 1 (calendar months, inclusive)
                          const months = employer.jobEndDate
                            ? employmentMonthsAtJob(
                              displayValue,
                              employer.jobEndDate,
                            )
                            : 0;
                          // Set start date + months; gaps are recomputed centrally by the job-gap effect.
                          setPreviousEmployers((prev) =>
                            prev.map((emp) =>
                              emp.id === employer.id
                                ? {
                                    ...emp,
                                    jobStartDate: displayValue,
                                    monthsAtJob: months.toString(),
                                  }
                                : emp,
                            ),
                          );
                          if (
                            previousEmployerErrors[employer.id]?.jobStartDate
                          ) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.jobStartDate;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                          // Clear monthsAtJob error when date is selected and months are calculated
                          if (
                            months > 0 &&
                            previousEmployerErrors[employer.id]?.monthsAtJob
                          ) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.monthsAtJob;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                        }}
                        onBlur={() => {
                          // Surface notfutureDateValidator on blur (Angular marks touched on blur).
                          if (!employer.jobStartDate) return; // required stays handled on save
                          setPreviousEmployerErrors((prev) => {
                            const fieldErrors = { ...(prev[employer.id] ?? {}) };
                            if (violatesNotFutureDate(employer.jobStartDate)) {
                              fieldErrors.jobStartDate =
                                "Date should not be in the Future.";
                            } else {
                              delete fieldErrors.jobStartDate;
                            }
                            return { ...prev, [employer.id]: fieldErrors };
                          });
                        }}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        allowFutureDates={false}
                        showHelperText={false}
                        disabled={isReadOnly}
                        error={previousEmployerErrors[employer.id]?.jobStartDate}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Job End Date*
                      </label>
                      <DatePicker
                        value={convertToDateInputFormat(employer.jobEndDate)}
                        onChange={(value) => {
                          const displayValue = convertToDisplayFormat(value);

                          if (employer.jobStartDate && displayValue) {
                            const start = new Date(convertToAPIDateFormat(employer.jobStartDate));
                            const end = new Date(convertToAPIDateFormat(displayValue));
                            start.setHours(0, 0, 0, 0);
                            end.setHours(0, 0, 0, 0);
                            if (start > end) {
                              updatePreviousEmployer(employer.id, "jobEndDate", "");
                              setValidationModalMessage("Job End Date should be after Job Start Date");
                              setShowValidationModal(true);
                              return;
                            }
                          }

                          // Angular: job_duration = moment(end).diff(start, 'months') + 1 (calendar months, inclusive)
                          const months = employmentMonthsAtJob(
                            employer.jobStartDate,
                            displayValue,
                          );

                          // Set end date + months; gap + explanation are recomputed centrally by the job-gap effect.
                          setPreviousEmployers((prev) =>
                            prev.map((emp) =>
                              emp.id === employer.id
                                ? {
                                  ...emp,
                                  jobEndDate: displayValue,
                                  monthsAtJob: months.toString(),
                                }
                                : emp,
                            ),
                          );
                          if (previousEmployerErrors[employer.id]?.jobEndDate) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.jobEndDate;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                          // Clear monthsAtJob error when end date is selected and months are calculated
                          if (
                            months > 0 &&
                            previousEmployerErrors[employer.id]?.monthsAtJob
                          ) {
                            setPreviousEmployerErrors((prev) => {
                              const newErrors = { ...prev[employer.id] };
                              delete newErrors.monthsAtJob;
                              return { ...prev, [employer.id]: newErrors };
                            });
                          }
                        }}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        allowFutureDates={false}
                        showHelperText={false}
                        disabled={isReadOnly}
                        error={
                          validateForm
                            ? previousEmployerErrors[employer.id]?.jobEndDate
                            : undefined
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Number of months at this job*{" "}
                        <span className="text-xs text-gray-500 font-normal">
                          (Auto-calculated)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={employer.monthsAtJob}
                        onChange={(e) =>
                          updatePreviousEmployer(
                            employer.id,
                            "monthsAtJob",
                            e.target.value,
                          )
                        }
                        placeholder="Auto-calculated from dates"
                        className={`w-full px-3 py-2 border rounded-md bg-gray-50 ${validateForm &&
                          previousEmployerErrors[employer.id]?.monthsAtJob
                          ? "border-red-500"
                          : "border-gray-300"
                          }`}
                        readOnly={true}
                        disabled={true}
                      />
                      {validateForm &&
                        previousEmployerErrors[employer.id]?.monthsAtJob && (
                          <div className="mt-1 text-sm text-red-600">
                            {previousEmployerErrors[employer.id].monthsAtJob}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Gap between this and current/previous Job + Website (for Self-Employed) */}
                  {employer.jobGap !== undefined &&
                    employer.jobGap !== null && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Gap between this and{" "}
                              {index === 0 ? "current Job" : "previous job"}
                            </label>
                            <input
                              type="number"
                              value={employer.jobGap.toString()}
                              readOnly={true}
                              disabled={true}
                              placeholder="Job Gap"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                            />
                          </div>

                          {/* Website field - only show for Self-Employed */}
                          {employer.employmentType === "self-employed" && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Website
                              </label>
                              <input
                                type="url"
                                value={employer.website || ""}
                                onChange={(e) =>
                                  updatePreviousEmployer(
                                    employer.id,
                                    "website",
                                    e.target.value,
                                  )
                                }
                                placeholder="https://example.com"
                                disabled={isReadOnly}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                              />
                            </div>
                          )}
                        </div>

                        {/* Job Gap Explanation - Show only when gap is 1 month or more (hidden when gap is 0) */}
                        {employer.jobGap >= 1 && (
                          <div className="mb-4 max-w-md">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Job Gap Explanation*
                            </label>
                            <textarea
                              value={employer.jobGapExplanation || ""}
                              onChange={(e) => {
                                updatePreviousEmployer(
                                  employer.id,
                                  "jobGapExplanation",
                                  e.target.value,
                                );
                                if (
                                  previousEmployerErrors[employer.id]
                                    ?.jobGapExplanation
                                ) {
                                  setPreviousEmployerErrors((prev) => {
                                    const newErrors = { ...prev[employer.id] };
                                    delete newErrors.jobGapExplanation;
                                    return {
                                      ...prev,
                                      [employer.id]: newErrors,
                                    };
                                  });
                                }
                              }}
                              placeholder="Job Gap Description"
                              rows={3}
                              maxLength={200}
                              disabled={isReadOnly}
                              className={`w-full px-3 py-2 border rounded-md bg-white ${validateForm &&
                                previousEmployerErrors[employer.id]
                                  ?.jobGapExplanation
                                ? "border-red-500"
                                : "border-gray-300"
                                } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                            />
                            {validateForm &&
                              previousEmployerErrors[employer.id]
                                ?.jobGapExplanation && (
                                <div className="mt-1 text-sm text-red-600">
                                  {
                                    previousEmployerErrors[employer.id]
                                      .jobGapExplanation
                                  }
                                </div>
                              )}
                          </div>
                        )}
                      </>
                    )}

                  {/* Website field - show standalone when Self-Employed but no job gap */}
                  {employer.employmentType === "self-employed" &&
                    (employer.jobGap === undefined ||
                      employer.jobGap === null) && (
                      <div className="mb-4 max-w-md">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          value={employer.website || ""}
                          onChange={(e) =>
                            updatePreviousEmployer(
                              employer.id,
                              "website",
                              e.target.value,
                            )
                          }
                          placeholder="https://example.com"
                          disabled={isReadOnly}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                        />
                      </div>
                    )}

                  {saveMessage[employer.id]?.text && (
                    <div
                      className={`mt-2 p-2 rounded-md text-sm ${saveMessage[employer.id].type === "success"
                        ? "bg-green-50 text-green-800"
                        : "bg-red-50 text-red-800"
                        }`}
                    >
                      {saveMessage[employer.id].text}
                    </div>
                  )}

                  {!isReadOnly && (
                    <div className="flex gap-3">
                      <button
                        type="button"
                        data-testid={`income-previous-employer-${index}-save-button`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleSavePreviousEmployer(employer);
                        }}
                        disabled={isSaving[employer.id]}
                        className="px-6 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving[employer.id] ? APPLICANT_CONTENT.application.messages.saving : APPLICANT_CONTENT.application.sections.personalProfile.save}
                      </button>
                      {!employer.jobId && (
                        <button
                          type="button"
                          data-testid={`income-previous-employer-${index}-cancel-button`}
                          onClick={() => removePreviousEmployer(employer.id)}
                          className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

            {hasPreviousEmployer === true && (
              <button
                type="button"
                data-testid="income-add-previous-employer-button"
                onClick={addPreviousEmployer}
                disabled={isReadOnly}
                className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Previous Employer
              </button>
            )}
          </div>

          {/* Other Sources of Income Section */}
          <div className="mb-8">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Other Sources of Income
            </h3>

            {otherIncomes.map((income, index) => (
              <div
                key={income.id}
                data-testid={`income-other-income-${index}`}
                className="mb-6 p-6 bg-white border border-gray-200 rounded-lg"
              >
                <div className="flex items-center mb-4 pb-3 border-b border-gray-200 relative">
                  <h4 className="text-sm font-semibold text-gray-900">
                    Other Income {index + 1}
                  </h4>
                  {income.id.startsWith("other-") && !isReadOnly && (
                    <button
                      type="button"
                      data-testid={`income-other-income-${index}-remove-button`}
                      onClick={() => removeOtherIncome(income.id)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium ml-auto flex-shrink-0"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Income Frequency*
                    </label>
                    <select
                      data-testid={`income-other-income-${index}-frequency-select`}
                      value={income.incomeFrequency}
                      onChange={(e) => {
                        const newFrequency = e.target
                          .value as OtherIncome["incomeFrequency"];
                        updateOtherIncome(
                          income.id,
                          "incomeFrequency",
                          newFrequency,
                        );
                        // Recalculate yearly income if income value exists
                        setOtherIncomes((prev) =>
                          prev.map((inc) => {
                            if (inc.id === income.id && inc.income) {
                              const yearly = calculateYearlyIncome(
                                inc.income,
                                newFrequency,
                              );
                              return {
                                ...inc,
                                incomeFrequency: newFrequency,
                                yearlyCalculatedIncome: yearly,
                              };
                            }
                            return inc.id === income.id
                              ? { ...inc, incomeFrequency: newFrequency }
                              : inc;
                          }),
                        );
                      }}
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border rounded-md bg-white ${otherIncomeErrors[income.id]?.incomeFrequency
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                        } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                    >
                      <option value="">Select Frequency</option>
                      <option value="monthly">Monthly</option>
                      {/* Reverted: income period options only include monthly and one-time */}
                      <option value="one-time">One Time</option>
                    </select>
                    {otherIncomeErrors[income.id]?.incomeFrequency && (
                      <p className="mt-1 text-sm text-red-600">
                        {otherIncomeErrors[income.id].incomeFrequency}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Source Name*
                    </label>
                    <input
                      type="text"
                      value={income.sourceName}
                      onChange={(e) =>
                        updateOtherIncome(
                          income.id,
                          "sourceName",
                          e.target.value,
                        )
                      }
                      placeholder="Source Name"
                      disabled={isReadOnly}
                      className={`w-full px-3 py-2 border rounded-md bg-white ${otherIncomeErrors[income.id]?.sourceName
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : "border-gray-300"
                        } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                    />
                    {otherIncomeErrors[income.id]?.sourceName && (
                      <p className="mt-1 text-sm text-red-600">
                        {otherIncomeErrors[income.id].sourceName}
                      </p>
                    )}
                  </div>

                  {/* Show different fields based on frequency */}
                  {income.incomeFrequency === "one-time" ? (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Amount*
                        </label>
                        <input
                          type="text"
                          value={formatCurrencyInput(
                            income.amount || income.income,
                          )}
                          onChange={(e) => {
                            // Extract numeric value from input (remove dollar sign and formatting)
                            const numericValue = getNumericValue(
                              e.target.value,
                            );
                            updateOtherIncome(
                              income.id,
                              "amount",
                              numericValue,
                            );
                            updateOtherIncome(
                              income.id,
                              "income",
                              numericValue,
                            );
                            // For one-time, yearly is same as amount
                            setOtherIncomes((prev) =>
                              prev.map((inc) =>
                                inc.id === income.id
                                  ? {
                                    ...inc,
                                    amount: numericValue,
                                    income: numericValue,
                                    yearlyCalculatedIncome:
                                      formatCurrencyInput(numericValue),
                                  }
                                  : inc,
                              ),
                            );
                          }}
                          onBlur={() => {
                            // Format with decimals when user leaves the field
                            const amountValue = income.amount || income.income;
                            if (amountValue) {
                              const numericValue = getNumericValue(formatCurrencyInput(amountValue, true));
                              setOtherIncomes((prev) =>
                                prev.map((inc) =>
                                  inc.id === income.id
                                    ? {
                                      ...inc,
                                      amount: numericValue,
                                      income: numericValue,
                                    }
                                    : inc,
                                ),
                              );
                            }
                          }}
                          placeholder="$0"
                          disabled={isReadOnly}
                          maxLength={10}
                          className={`w-full px-3 py-2 border rounded-md bg-white ${otherIncomeErrors[income.id]?.income ||
                            otherIncomeErrors[income.id]?.amount
                            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300"
                            } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                        />
                        {(otherIncomeErrors[income.id]?.income ||
                          otherIncomeErrors[income.id]?.amount) && (
                            <p className="mt-1 text-sm text-red-600">
                              {otherIncomeErrors[income.id].income ||
                                otherIncomeErrors[income.id].amount}
                            </p>
                          )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transaction Date*
                        </label>
                        <DatePicker
                          value={convertToDateInputFormat(
                            income.transactionDate,
                          )}
                          onChange={(value) => {
                            const displayValue = convertToDisplayFormat(value);
                            updateOtherIncome(
                              income.id,
                              "transactionDate",
                              displayValue,
                            );
                          }}
                          placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                          allowFutureDates={false}
                          showHelperText={false}
                          disabled={isReadOnly}
                          error={otherIncomeErrors[income.id]?.transactionDate}
                        />
                        {otherIncomeErrors[income.id]?.transactionDate && (
                          <p className="mt-1 text-sm text-red-600">
                            {otherIncomeErrors[income.id].transactionDate}
                          </p>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Income*
                        </label>
                        <input
                          type="text"
                          value={formatCurrencyInput(income.income)}
                          onChange={(e) => {
                            // Extract numeric value from input (remove dollar sign and formatting)
                            const numericValue = getNumericValue(
                              e.target.value,
                            );
                            updateOtherIncome(
                              income.id,
                              "income",
                              numericValue,
                            );
                            const yearly = calculateYearlyIncome(
                              numericValue,
                              income.incomeFrequency,
                            );
                            setOtherIncomes((prev) =>
                              prev.map((inc) =>
                                inc.id === income.id
                                  ? {
                                    ...inc,
                                    income: numericValue,
                                    yearlyCalculatedIncome: yearly,
                                  }
                                  : inc,
                              ),
                            );
                          }}
                          onBlur={() => {
                            // Format with decimals when user leaves the field
                            if (income.income) {
                              const numericValue = getNumericValue(formatCurrencyInput(income.income, true));
                              setOtherIncomes((prev) =>
                                prev.map((inc) =>
                                  inc.id === income.id
                                    ? { ...inc, income: numericValue }
                                    : inc,
                                ),
                              );
                            }
                          }}
                          placeholder="$0"
                          disabled={isReadOnly}
                          maxLength={10}
                          className={`w-full px-3 py-2 border rounded-md bg-white ${otherIncomeErrors[income.id]?.income
                            ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                            : "border-gray-300"
                            } ${isReadOnly ? "bg-gray-100 cursor-not-allowed opacity-50" : ""}`}
                        />
                        {otherIncomeErrors[income.id]?.income && (
                          <p className="mt-1 text-sm text-red-600">
                            {otherIncomeErrors[income.id].income}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Yearly Calculated Income
                        </label>
                        <input
                          type="text"
                          value={income.yearlyCalculatedIncome}
                          readOnly={true}
                          disabled={true}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Show period-based fields (start/end date) for all frequencies except one-time */}
                {income.incomeFrequency !== "one-time" && income.incomeFrequency !== "" && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Start Date*
                      </label>
                      <DatePicker
                        value={convertToDateInputFormat(income.startDate)}
                        onChange={(value) => {
                          const displayValue = convertToDisplayFormat(value);
                          updateOtherIncome(
                            income.id,
                            "startDate",
                            displayValue,
                          );
                          // Calculate months of income (match Angular job_duration: calendar months + 1)
                          if (income.endDate || income.currentlyActive) {
                            const endDate = income.currentlyActive
                              ? new Date().toISOString().split("T")[0]
                              : income.endDate;
                            const months = employmentMonthsAtJob(
                              displayValue,
                              endDate ?? undefined,
                            );
                            updateOtherIncome(
                              income.id,
                              "monthsOfIncome",
                              months > 0 ? months.toString() : "",
                            );
                          }
                        }}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        allowFutureDates={false}
                        showHelperText={false}
                        disabled={isReadOnly}
                        error={otherIncomeErrors[income.id]?.startDate}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        End Date
                      </label>
                      <DatePicker
                        value={convertToDateInputFormat(income.endDate)}
                        onChange={(value) => {
                          const displayValue = convertToDisplayFormat(value);
                          updateOtherIncome(income.id, "endDate", displayValue);
                          // Calculate months of income (match Angular: calendar months + 1)
                          if (income.startDate) {
                            const months = employmentMonthsAtJob(
                              income.startDate,
                              displayValue,
                            );
                            updateOtherIncome(
                              income.id,
                              "monthsOfIncome",
                              months > 0 ? months.toString() : "",
                            );
                          }
                        }}
                        placeholder={APPLICANT_CONTENT.application.sections.personalProfile.fields.placeholder.dateOfBirth}
                        disabled={isReadOnly || income.currentlyActive}
                        allowFutureDates={false}
                        showHelperText={false}
                        error={otherIncomeErrors[income.id]?.endDate}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        &nbsp;
                      </label>
                      <label className="flex items-center h-[42px]">
                        <input
                          type="checkbox"
                          checked={income.currentlyActive}
                          onChange={(e) => {
                            if (!isReadOnly) {
                              updateOtherIncome(
                                income.id,
                                "currentlyActive",
                                e.target.checked,
                              );
                              if (e.target.checked) {
                                updateOtherIncome(income.id, "endDate", "");
                                // Recalculate months if start date exists (match Angular: calendar months + 1)
                                if (income.startDate) {
                                  const months = employmentMonthsAtJob(
                                    income.startDate,
                                    new Date().toISOString().split("T")[0],
                                  );
                                  updateOtherIncome(
                                    income.id,
                                    "monthsOfIncome",
                                    months > 0 ? months.toString() : "",
                                  );
                                }
                              }
                            }
                          }}
                          disabled={isReadOnly}
                          className={`mr-2 ${isReadOnly ? "cursor-not-allowed opacity-50" : ""}`}
                        />
                        <span className="text-sm text-gray-700">
                          OR Currently Active
                        </span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Months of this Income*
                      </label>
                      <input
                        type="text"
                        value={income.monthsOfIncome}
                        readOnly={true}
                        disabled={true}
                        placeholder="Auto-calculated"
                        className={`w-full px-3 py-2 border rounded-md bg-gray-50 cursor-not-allowed ${otherIncomeErrors[income.id]?.monthsOfIncome
                          ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                          : "border-gray-300"
                          }`}
                      />
                      {otherIncomeErrors[income.id]?.monthsOfIncome && (
                        <p className="mt-1 text-sm text-red-600">
                          {otherIncomeErrors[income.id].monthsOfIncome}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {saveOtherIncomeMessage[income.id] &&
                  saveOtherIncomeMessage[income.id].type === "success" && (
                    <div className="mb-4 p-4 bg-green-100 border-2 border-green-400 rounded-lg shadow-md">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 text-green-600 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p className="text-sm font-semibold text-green-800">
                          {saveOtherIncomeMessage[income.id].text}
                        </p>
                      </div>
                    </div>
                  )}
                {saveOtherIncomeMessage[income.id] &&
                  saveOtherIncomeMessage[income.id].type === "error" && (
                    <div className="mb-4 p-4 bg-red-100 border-2 border-red-400 rounded-lg shadow-md">
                      <div className="flex items-center">
                        <svg
                          className="w-5 h-5 text-red-600 mr-2"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <p className="text-sm font-semibold text-red-800">
                          {saveOtherIncomeMessage[income.id].text}
                        </p>
                      </div>
                    </div>
                  )}

                {!isReadOnly && (
                  <div className="flex gap-3">
                    <button
                      type="button"
                      data-testid={`income-other-income-${index}-save-button`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSaveOtherIncome(income);
                      }}
                      disabled={isSavingOtherIncome[income.id]}
                      className="px-6 py-2 bg-[#1597ff] text-white rounded-md hover:bg-[#0d7acc] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingOtherIncome[income.id] ? APPLICANT_CONTENT.application.messages.saving : APPLICANT_CONTENT.application.sections.personalProfile.save}
                    </button>
                    {/* Show Cancel button only for new entries (not saved) */}
                    {!income.id.startsWith("other-") && (
                      <button
                        type="button"
                        data-testid={`income-other-income-${index}-cancel-button`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          removeOtherIncome(income.id);
                        }}
                        className="px-6 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}

            <button
              type="button"
              data-testid="income-add-other-income-button"
              onClick={addOtherIncome}
              disabled={isReadOnly}
              className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              Add Other Income
            </button>
          </div>
           {/* Link My Bank Section */}
           <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Bank Linking
            </h3>
            {/* Show Link My Bank button if no ninja accounts OR reconnect required */}
            {/* Match Angular condition: !ninjaAccounts?.length || (ninjaAccounts?.length && ninjaAccounts[0]?.ibv_report_status === 'reconnect_required') */}
            {shouldShowLinkMyBankForNinjaAccounts(bankLinking.ninjaAccounts ?? []) &&
              bankLinking.ninjaConfig && (
                <div className="p-6 bg-white border border-gray-200 rounded-lg">
                  {/* Bank Message Notification */}
                  {bankMessage && (
                    <div
                      className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${bankMessage.type === "success"
                        ? "bg-green-50 border border-green-200"
                        : "bg-red-50 border border-red-200"
                        }`}
                    >
                      {bankMessage.type === "success" ? (
                        <AlertTriangle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p
                          className={`text-sm font-medium ${bankMessage.type === "success"
                            ? "text-green-800"
                            : "text-red-800"
                            }`}
                        >
                          {bankMessage.text}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="p-3">
                    <button
                      type="button"
                      onClick={handleLinkMyBank}
                      disabled={
                        isReadOnly || bankLinking.isLoading || isGeneratingToken
                      }
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-md transition-colors font-medium ${isReadOnly ||
                        applicationData?.plaid_opt_out === "1" ||
                        bankLinking.isLoading ||
                        isGeneratingToken
                        ? "bg-[#1597ff] text-white cursor-not-allowed opacity-60"
                        : "bg-[#1597ff] text-white hover:bg-[#0d7acc]"
                        }`}
                    >
                      Link My Bank
                    </button>
                    {bankLinking.ninjaConfig?.NINJA_REQUIRED === "YES" && (
                      <p className="text-xs text-gray-600 mt-2">
                        <i>*required</i>
                      </p>
                    )}
                  </div>

                  <div className="ml-2">
                    <div className="pl-3">
                      <p className="text-sm text-gray-600 flex items-center gap-2">
                        <Info className="w-5 h-5 text-green-600 flex-shrink-0" />
                        Linking your bank is the best way for us to verify your
                        income and identity, and it provides us with the most
                        thorough and accurate information. This connection is a
                        one-time verification, and for your protection, we never
                        see or store your bank login details.
                      </p>
                    </div>
                  </div>

                </div>
              )}

            {/* Show linked banks section if ninja accounts exist */}
            {bankLinking.ninjaAccounts?.length > 0 && (
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
                <p className="text-base font-semibold text-blue-600 ml-3 mb-4">
                  Your Linked Bank:
                </p>

                {bankLinking.ninjaAccounts.map((account, index) => (
                  <div key={account.pli_id || index} className="ml-1 mb-2">
                    {account.ibv_report_status !== "initiated" ? (
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                          <span className="text-blue-600 text-lg">🏦</span>
                        </div>
                        <div>
                          <div className="text-base font-semibold text-gray-900">
                            {account.bank_name}
                          </div>
                          <div className="text-sm text-gray-600">
                            {account.account_type} :{" "}
                            {account.mask ? `****${account.mask}` : ""}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-base text-gray-700">
                        Processing Result
                      </div>
                    )}
                  </div>
                ))}

                <div className="p-2 mt-3">
                  <p className="text-sm text-gray-700">
                    Thank you for linking your bank with us! This helps us speed
                    things along for you and your data is secure. Please be
                    aware that we still may ask for additional proof of current
                    employment.
                  </p>
                </div>
              </div>
            )}

            {/* Show Plaid accounts if they exist (separate from Ninja) */}
            {bankLinking.plaidAccounts?.length > 0 && (
              <div className="p-6 bg-white border border-gray-200 rounded-lg mt-4">
                <p className="text-base font-semibold text-blue-600 ml-3 mb-4">
                  Your Linked Bank (Plaid):
                </p>

                {bankLinking.plaidAccounts.map((account, index) => (
                  <div key={account.pli_id || index} className="ml-1 mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded flex items-center justify-center">
                        <span className="text-blue-600 text-lg">🏦</span>
                      </div>
                      <div>
                        <div className="text-base font-semibold text-gray-900">
                          {account.ins_name || "Bank"}
                        </div>
                        <div className="text-sm text-gray-600">
                          {account.subtype} :{" "}
                          {account.mask ? `****${account.mask}` : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="p-2 mt-3">
                  <p className="text-sm text-gray-700">
                    Thank you for linking your bank with us! This helps us speed
                    things along for you and your data is secure. Please be
                    aware that we still may ask for additional proof of current
                    employment.
                  </p>
                </div>
              </div>
            )}

          </div>

          {/* Supporting Documents: NINJA_INCOME (no-bank) + INCOME (resident_application), single section */}
          {showSupportingUploadsUnifiedSection && (
            <div className="mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Supporting Documents
              </h3>
              <div className="p-6 bg-white border border-gray-200 rounded-lg">
              {/* <p className="text-sm text-gray-600 mb-4">
                {supportingUploadsDescriptor}
              </p> */}
              <ul className="list-disc list-inside text-sm text-gray-600 mb-4 space-y-2">
                <li>
                Please upload any supporting documentation requested by Liberty Rent here.
                </li>
              </ul>

              {showNinjaInlineUploadDropzone && (
                <div className="mb-6">
                  <IncomeDocumentationSection
                    omitOuterCard
                    omitDescriptor
                    hideFileList
                    showDropzone
                    onUploadFiles={handleInlineNinjaSupportingUpload}
                    existingUploadCount={ninjaIncomeExistingCount}
                    existingUploadNames={ninjaExistingFileNames}
                    maxSizeMb={ninjaProofMaxSizeMb}
                    uploading={bankLinking.isLoading}
                    message={null}
                    isAwaiting={false}
                    isReadOnly={false}
                    proofOfIncome={[]}
                    onPreview={() => {}}
                    onRemove={async () => {}}
                  />
                </div>
              )}

              {isAwaitingStatus && !isReadOnly && (
                <div className="mb-6">
                  <IncomeDocumentationSection
                    omitOuterCard
                    omitDescriptor
                    hideFileList
                    showDropzone
                    onUploadFiles={async (files) => {
                      const filtered = files.filter(isAcceptedProofOfIncomeFile);
                      if (filtered.length === 0) return;
                      await handleProofOfIncomeModalSubmit(filtered);
                    }}
                    existingUploadCount={
                      Array.isArray(applicationData?.proof_of_income)
                        ? applicationData.proof_of_income.length
                        : 0
                    }
                    existingUploadNames={
                      Array.isArray(applicationData?.proof_of_income)
                        ? (
                            applicationData.proof_of_income as {
                              filename?: string;
                              name?: string;
                            }[]
                          ).map((f) => f.filename || f.name || "")
                        : []
                    }
                    maxSizeMb={proofOfIncomeMaxMb}
                    uploading={proofOfIncomeUploading}
                    isAwaiting={isAwaitingStatus}
                    message={proofOfIncomeMessage}
                    isReadOnly={!!isReadOnly}
                    proofOfIncome={applicationData?.proof_of_income ?? []}
                    onPreview={handleProofOfIncomeFilePreview}
                    onRemove={handleRemoveProofOfIncomeFile}
                  />
                </div>
              )}

              {showNinjaDocSubsection && (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50 mb-4">

                  <p className="text-sm font-semibold text-gray-800 mb-2">
                   Income Verification Documents
                  </p>

                  {hasNinjaIncomeProofDocs && (
                    <div className="pt-1">
                      <div className="space-y-2">
                        {applicationData!.ninja_income_proof!.map(
                          (file: ApplicationFileAttachment, index: number) => {
                            const rawFileName =
                              file.filename || file.name || "document";
                            const fileName =
                              typeof rawFileName === "string"
                                ? rawFileName.replace(/^[a-f0-9]{8,}-/i, "")
                                : "document";
                            const uploadedBy =
                              typeof file.name === "string" &&
                              file.name.trim() !== ""
                                ? file.name.trim()
                                : "";
                            let uploadedAtText = "";
                            if (
                              file.created_at !== undefined &&
                              file.created_at !== null &&
                              `${file.created_at}`.trim() !== ""
                            ) {
                              const createdAtNum = Number(file.created_at);
                              if (Number.isFinite(createdAtNum)) {
                                const createdDate = new Date(
                                  createdAtNum > 1000000000000
                                    ? createdAtNum
                                    : createdAtNum * 1000,
                                );
                                if (!Number.isNaN(createdDate.getTime())) {
                                  const yyyy = createdDate.getFullYear();
                                  const mm = `${createdDate.getMonth() + 1}`.padStart(
                                    2,
                                    "0",
                                  );
                                  const dd = `${createdDate.getDate()}`.padStart(
                                    2,
                                    "0",
                                  );
                                  const hh = `${createdDate.getHours()}`.padStart(
                                    2,
                                    "0",
                                  );
                                  const min = `${createdDate.getMinutes()}`.padStart(
                                    2,
                                    "0",
                                  );
                                  const ss = `${createdDate.getSeconds()}`.padStart(
                                    2,
                                    "0",
                                  );
                                  uploadedAtText =
                                    `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
                                } else {
                                  uploadedAtText = `${file.created_at}`.trim();
                                }
                              } else {
                                uploadedAtText = `${file.created_at}`.trim();
                              }
                            }
                            const uploadedMetaText =
                              uploadedBy || uploadedAtText
                                ? `Uploaded - ${uploadedBy}${
                                    uploadedBy && uploadedAtText ? " " : ""
                                  }${uploadedAtText}`.trim()
                                : "";
                            const isDeleting =
                              deletingNinjaFileId === file.attachment_id;

                            return (
                              <div
                                key={file.attachment_id || index}
                                className={`flex items-center gap-2 hover:bg-gray-50 p-2 rounded transition-colors ${
                                  isDeleting ? "opacity-60" : ""
                                }`}
                              >
                                <div className="flex-3 min-w-0">
                                  <span className="text-sm text-gray-700 block truncate">
                                    {fileName}
                                  </span>
                                  {uploadedMetaText && (
                                    <span className="text-xs text-gray-500 block mt-0.5 truncate">
                                      {uploadedMetaText}
                                    </span>
                                  )}
                                </div>
                                {!isDeleting && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFilePreview({
                                        filepath: file.filepath,
                                        name: fileName,
                                        attachment_id: file.attachment_id,
                                      });
                                    }}
                                    className="p-1 text-blue-600 hover:bg-blue-50 rounded flex-shrink-0"
                                    title="View"
                                    aria-label={`View ${fileName}`}
                                  >
                                    <Eye className="h-5 w-5" />
                                  </button>
                                )}
                                {!isReadOnly &&
                                  file.attachment_id &&
                                  applicationData?.applicant_status ===
                                    "INCOMPLETE" && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (!isDeleting && file.attachment_id) {
                                          handleRemoveNinjaIncomeProofFile(
                                            file.attachment_id,
                                          );
                                        }
                                      }}
                                      disabled={isDeleting}
                                      className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 flex-shrink-0"
                                      title={
                                        isDeleting ? "Deleting..." : "Delete"
                                      }
                                      aria-label={
                                        isDeleting
                                          ? "Deleting file"
                                          : `Delete ${fileName}`
                                      }
                                    >
                                      {isDeleting ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-red-500" />
                                      ) : (
                                        <Trash className="h-4 w-4" />
                                      )}
                                    </button>
                                  )}
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {hasProofOfIncomeDocs && (
                <div className="mt-2">
                  <p className="text-sm font-semibold text-gray-800 mb-2">
                  Additional Supporting Documents
                  </p>
                  <IncomeDocumentationSection
                    filesOnly
                    isAwaiting={isAwaitingStatus}
                    message={null}
                    isReadOnly={!!isReadOnly}
                    proofOfIncome={applicationData?.proof_of_income ?? []}
                    onPreview={handleProofOfIncomeFilePreview}
                    onRemove={handleRemoveProofOfIncomeFile}
                  />
                </div>
              )}
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 pt-4 pb-6">
          <button
            data-testid="income-back-button"
            type="button"
            onClick={() => {
              const propertyPath = applicantId
                ? getTabPathWithApplicantId("property", applicantId)
                : "/applicant/application/property";
              if (applicantId) {
                navigate(propertyPath, getTabNavigationOptions("property"));
              } else {
                navigate(propertyPath);
              }
            }}
            disabled={isSavingAndContinuing || isSavingExit}
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {APPLICANT_CONTENT.application.buttons.back}
          </button>
          {!isReadOnly && (
            <button
              data-testid="income-save-continue-button"
              onClick={() => handleSaveAndContinue()}
              disabled={
                isSavingAndContinuing ||
                isSavingExit ||
                isRecalculatingDistances ||
                !applicationData?.applicant_id
              }
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSavingAndContinuing || isRecalculatingDistances ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {APPLICANT_CONTENT.application.buttons.saveContinue}
                </>
              )}
            </button>
          )}
          {isReadOnly && (
            <button
              data-testid="income-next-button"
              onClick={() => {
                const historyPath = applicantId
                  ? getTabPathWithApplicantId("history", applicantId)
                  : "/applicant/application/history";
                if (applicantId) {
                  navigate(historyPath, getTabNavigationOptions("history"));
                } else {
                  navigate(historyPath);
                }
              }}
              className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-md hover:shadow-lg"
            >
              {APPLICANT_CONTENT.applyNow.step3.nextButton}
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          <button
            data-testid="income-save-exit-button"
            onClick={() => {
              if (isReadOnly) {
                navigate("/applicant/dashboard", { replace: true });
              } else {
                handleSaveAndContinue(true);
              }
            }}
            disabled={
              !isReadOnly &&
              (isSavingExit ||
                isSavingAndContinuing ||
                isRecalculatingDistances ||
                !applicationData?.applicant_id)
            }
            className="inline-flex items-center gap-2 bg-[#1597ff] hover:bg-[#0d7acc] text-white font-medium px-8 py-3 rounded-lg transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSavingExit || isRecalculatingDistances ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : isReadOnly ? (
              APPLICANT_CONTENT.application.buttons.exit
            ) : (
              APPLICANT_CONTENT.application.buttons.saveExit
            )}
          </button>
        </div>
      </div>

      {/* Bank Linking Modals */}
      <LinkBankModal
        isOpen={showLinkBankModal}
        onClose={() => setShowLinkBankModal(false)}
        onLinkBank={handleLinkBankFromModal}
        onUseDocumentUploads={handleUseDocumentUploadsFromModal}
        onReturnToApplication={() => setShowLinkBankModal(false)}
      />
      {showBankDetailsModal && applicationData?.applicant_id && (
        <BankDetailsModal
          key={`bank-upload-modal-${applicationData.applicant_id}`}
          isOpen={showBankDetailsModal}
          onClose={() => {
            setShowBankDetailsModal(false);
            setChoseDocumentUploads(true);
          }}
          onSubmit={handleBankDetailsModalSubmit}
          isSubmitting={bankLinking.isLoading}
          existingBankName={applicationData.bank_name || undefined}
          existingFileCount={
            Array.isArray(applicationData.ninja_income_proof)
              ? applicationData.ninja_income_proof.length
              : 0
          }
          existingFileNames={
            Array.isArray(applicationData.ninja_income_proof)
              ? (
                  applicationData.ninja_income_proof as {
                    filename?: string;
                    name?: string;
                  }[]
                ).map((f) => f.filename || f.name || "")
              : []
          }
          maxSizeMb={
            applicationData.applicant_status === "AWAITING" ||
            applicationData.application_status === "AWAITING"
              ? 10
              : 5
          }
        />
      )}

      {/* Ninja IBV (Angular-style) Popup */}
      {showNinjaIframePopup &&
        applicationData?.applicant_id &&
        (() => {
          const applicantId = applicationData.applicant_id as string | number;
          return (
            <NinjaIframePopup
              applicantId={applicantId}
              isOpen={showNinjaIframePopup}
              ninjaVersion={bankLinking.ninjaConfig?.NINJA_VERSION}
              responseData={ninjaIbvResponseData}
              isLoading={isGeneratingToken}
              onClose={() => {
                setShowNinjaIframePopup(false);
                setNinjaIbvResponseData(null);
              }}
              onSuccess={async (data) => {
                const publicToken = data.product_request_id || data.request_id;
                if (!publicToken) {
                  setBankMessage({
                    type: "error",
                    text: "No product request ID received. Please try again.",
                  });
                  setTimeout(() => setBankMessage(null), 5000);
                  return;
                }
                try {
                  await linkNinjaAccount(applicantId, publicToken);
                  await bankLinking.refreshAccounts();
                  await dispatch(fetchApplicationById(applicantId));

                  setBankMessage({
                    type: "success",
                    text: "Bank account linked successfully!",
                  });
                  setTimeout(() => setBankMessage(null), 5000);
                  setShowNinjaIframePopup(false);
                  setNinjaIbvResponseData(null);
                } catch (e: unknown) {
                  const msg =
                    e instanceof Error
                      ? e.message
                      : "Failed to link account. Please try again.";
                  setBankMessage({ type: "error", text: msg });
                  setTimeout(() => setBankMessage(null), 5000);
                }
              }}
            />
          );
        })()}

      {/* Validation Modal */}
      <ValidationModal
        {...applicantValidationModalProps}
        isOpen={showValidationModal}
        title={APPLICANT_CONTENT.errors.missingFields}
        message={validationModalMessage}
        onClose={() => setShowValidationModal(false)}
      />
      <NoEmploymentModal
        isOpen={showNoEmploymentModal}
        onClose={() => setShowNoEmploymentModal(false)}
        onConfirm={() => {
          setShowNoEmploymentModal(false);
          handleSaveAndContinue(false, true); // (exitAfterSave=false, confirmNoEmployment=true)
        }}
      />
      <SixFileUploadLimitModal
        isOpen={showSixFileUploadLimitModal}
        onClose={() => setShowSixFileUploadLimitModal(false)}
      />
      {/* Import from previous application — employment (Angular importOldata() / setOldApplicationData) */}
      <ConfirmDialog
        {...applicantConfirmDialogProps}
        isOpen={showImportPreviousEmploymentDialog}
        title="Import from Previous Application"
        message="We found some employment records from your previous application with us. Would you like us to import these?"
        primaryLabel="Yes, import as current employment"
        onPrimary={() => applyEmploymentImport("current")}
        neutralLabel="Yes, import as previous employment"
        onNeutral={() => applyEmploymentImport("previous")}
        secondaryLabel="No, thank you"
        onSecondary={() => setShowImportPreviousEmploymentDialog(false)}
        variant="info"
        size="wide"
        primaryFirst
        equalWidthActions
      />
      </div>
    </div>
  );
}